// ═══════════════════════════════════════════════════════════
// CustomerModal — модальне вікно клієнта
// Перегляд/редагування профілю, вкладки: основне, візити, фінанси, документи
// Валідація телефону/email, історія відвідувань, баланс, Telegram-зв'язок
// ═══════════════════════════════════════════════════════════
import React, { useState, useRef, useEffect } from 'react';
import { validatePhone, validateEmail, formatPhone } from '../utils/validation';
import { useT } from '../contexts/I18nContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../services/api';
import { getChannelsStatus } from '../services/channelsStatus';
import { useCustomerSources } from '../services/customerSources';
import { saveDoc, removeDoc } from '../firestoreService';
import { saveTransactions } from '../utils/transactionApi';
import { uploadImage, uploadFile } from '../services/uploadImage';
import { usePresence } from '../hooks/usePresence';
import { useCustomerSearch } from '../hooks/useCustomerSearch';
import { useAppData } from '../contexts/DataContext';
import CustomerTasksTab from './tasks/CustomerTasksTab';
import CustomerCallsTab from './calls/CustomerCallsTab';
import DocumentFromTemplateModal from './documents/DocumentFromTemplateModal';
import DocxPreviewModal from './documents/DocxPreviewModal';
import { isPreviewableDocx } from './documents/docFiles';
import CustomerComments from './CustomerComments';

// Categories and importance are now passed as props from App.js
const GENDER_KEYS = [
  { value: 'female', key: 'custModal.genderFemale' },
  { value: 'male',   key: 'custModal.genderMale' },
  { value: 'other',  key: 'custModal.genderOther' },
];
const MONTH_KEYS = [
  { value: '01', key: 'custModal.monthJan' },  { value: '02', key: 'custModal.monthFeb' },
  { value: '03', key: 'custModal.monthMar' },{ value: '04', key: 'custModal.monthApr' },
  { value: '05', key: 'custModal.monthMay' }, { value: '06', key: 'custModal.monthJun' },
  { value: '07', key: 'custModal.monthJul' },  { value: '08', key: 'custModal.monthAug' },
  { value: '09', key: 'custModal.monthSep' },{ value: '10', key: 'custModal.monthOct' },
  { value: '11', key: 'custModal.monthNov' },{ value: '12', key: 'custModal.monthDec' },
];

/** Нік Instagram, як на сервері (api/_lib/handles.js): без @, нижній регістр. */
const normalizeHandle = (h) => {
  const s = String(h ?? '').trim().replace(/^(https?:\/\/)?(www\.)?instagram\.com\//i, '')
    .replace(/[/?#].*$/, '').replace(/^@+/, '').toLowerCase();
  return /^[a-z0-9._]{1,30}$/.test(s) ? s : null;
};

const emptyForm = {
  name: '', phone: '', additionalPhone: '', email: '', instagram: '',
  category: 'Новий клієнт', gender: 'female', importance: 'Середня',
  discount: 0, cardNumber: '',
  birthDay: '', birthMonth: '', birthYear: '',
  referredBy: '',
  note: '', comments: '', forbidOnlineBooking: false,
  avatar: null, sold: 0, paid: 0,
};

function getInitials(name) {
  return name.trim().split(/\s+/).map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
}

function SectionTitle({ children }) {
  return <div className="section-title">{children}</div>;
}

function FormField({ label, error, children, full }) {
  return (
    <div className={`form-field${full ? ' full' : ''}`}>
      {label && <label className="field-label">{label}</label>}
      {children}
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}

function ReferralSearch({ excludeId, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const ref = useRef(null);

  // Resolve the selected customer (by id) server-side so we don't need
  // the full customers array in-memory just to display the currently
  // chosen referrer's name.
  useEffect(() => {
    if (!value) { setSelected(null); return; }
    let cancelled = false;
    apiFetch(`/api/analytics/customers-by-ids?ids=${encodeURIComponent(String(value))}`)
      .then(r => { if (!cancelled) setSelected(r?.customers?.[0] || null); })
      .catch(() => { if (!cancelled) setSelected(null); });
    return () => { cancelled = true; };
  }, [value]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Server-side autocomplete; filter out the currently-edited customer
  // on the client since the endpoint doesn't know about it.
  const { results: searchResults } = useCustomerSearch(query, { limit: 8, enabled: open });
  const filtered = excludeId != null
    ? searchResults.filter(c => String(c.id) !== String(excludeId))
    : searchResults;

  if (selected && !open) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ flex: 1 }}>{selected.name || selected.phone || selected.id}</span>
        <button type="button" className="btn-icon" style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}
          onClick={() => { onChange(''); setQuery(''); }}>✕</button>
      </div>
    );
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        type="text" value={query} placeholder="Пошук клієнта..."
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => query.trim() && setOpen(true)}
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <div className="dropdown-list" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, maxHeight: 200, overflowY: 'auto', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,.12)' }}>
          {filtered.map(c => (
            <div key={c.id} className="dropdown-item" style={{ padding: '8px 12px', cursor: 'pointer' }}
              onMouseDown={() => { onChange(String(c.id)); setQuery(''); setOpen(false); }}>
              <span style={{ fontWeight: 500 }}>{c.name || '—'}</span>
              {c.phone && <span style={{ marginLeft: 8, color: 'var(--text-muted)', fontSize: '0.85em' }}>{c.phone}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Форма наявного клієнта. Поля, яких у запису нема, не підставляємо
// порожніми: порожнє значення у формі означає «очистити» (24.10).
function customerToForm(c) {
  return {
    ...c,
    phone: formatPhone(c.phone || ''),
    ...(c.additionalPhone !== undefined ? { additionalPhone: formatPhone(c.additionalPhone || '') } : {}),
    ...(c.instagram !== undefined ? { instagram: c.instagram || '' } : {}),
  };
}

// Нова картка з анонімного треду «Комунікації»: форма відкривається вже
// заповненою тим, що відомо про відвідувача. Беремо лише ці поля.
const PREFILL_FIELDS = ['name', 'phone', 'email', 'instagram', 'avatar', 'source'];

function CustomerModal({ customer, prefill, categories, importanceLevels, appointments, transactions, staff, services, orders, onSave, onClose, onOpenAppointment, initialTab, initialMode, loyaltyPrograms, loyaltyBalances, loyaltyTransactions, onLoyaltyBalancesChange, onLoyaltyTransactionsChange, subscriptionPlans, onTransactionsChange, stockMovements, products }) {
  const { t } = useT();
  const { tenantId, currentUser, userProfile, hasPermission } = useAuth();
  const { lockCollection, unlockCollection, ensureGroups, activeLocationId } = useAppData();
  const txAuthor = currentUser ? { uid: currentUser.uid, name: userProfile?.displayName || currentUser.email || '' } : null;

  // Ensure required data groups are loaded when modal opens so
  // Financial/Analytics tabs work if user navigated directly to Clients.
  // Includes 'loyalty' for subscription visit price calculations.
  //
  // History note: we DON'T widen the global DataContext appointments/
  // transactions/orders window here anymore — customer-specific history
  // comes from /api/analytics/customer-detail (fetched below into
  // sqlDetail) which is bounded to this one customer regardless of tenant
  // size. That keeps the modal safe even on Large tenants where the
  // tenant-wide DataContext arrays are capped at 30 days.
  useEffect(() => {
    ensureGroups('calendar', 'finance', 'inventory', 'loyalty');
  }, [ensureGroups]);
  const defaultCategory = (categories && categories[0]?.name) || 'Новий клієнт';

  // View/Edit mode — new customers always open in edit
  const [editMode, setEditMode] = useState(!customer || initialMode === 'edit');

  // Presence: block parallel editing of the same customer
  const toast = useToast();
  const { editingUser, isLocked } = usePresence('customer', customer?.id ?? null);

  // Lock the customers collection only when editing
  useEffect(() => {
    if (!customer?.id || !editMode) return;
    lockCollection('customers');
    return () => { unlockCollection('customers'); };
  }, [customer?.id, editMode, lockCollection, unlockCollection]);
  const defaultImportance = (importanceLevels && importanceLevels[0]?.name) || 'Середня';
  const [form, setForm] = useState(() => {
    if (customer) return customerToForm(customer);
    const fromThread = {};
    for (const k of PREFILL_FIELDS) if (prefill?.[k]) fromThread[k] = prefill[k];
    if (fromThread.phone) fromThread.phone = formatPhone(fromThread.phone);
    return { ...emptyForm, category: defaultCategory, importance: defaultImportance, ...fromThread };
  });
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState(initialTab || 'main');
  // Кнопка «Звʼязатися пізніше» в шапці відкриває форму задачі у вкладці «Задачі».
  const [contactLaterSignal, setContactLaterSignal] = useState(0);
  // SQL-backed customer detail — all data for all tabs in one request.
  const [sqlDetail, setSqlDetail] = useState(null);
  useEffect(() => {
    if (!customer?.id) { setSqlDetail(null); return; }
    let cancelled = false;
    const phoneParam = customer.phone ? `&customerPhone=${encodeURIComponent(customer.phone)}` : '';
    apiFetch(`/api/analytics/customer-detail?customerId=${encodeURIComponent(customer.id)}${phoneParam}`)
      .then(res => { if (!cancelled && res.ok) setSqlDetail(res); })
      .catch(() => { if (!cancelled) setSqlDetail(null); });
    return () => { cancelled = true; };
  }, [customer?.id, customer?.phone]);

  // Loyalty ledger for THIS customer (not the tenant-wide loyaltyTransactions
  // array). Bounded by customer, so Large tenants with 100K+ loyalty
  // transactions don't need to ship the full ledger to render a customer
  // card. Empty until customer.id is known; re-fetches when customer changes.
  const [customerLoyaltyTx, setCustomerLoyaltyTx] = useState([]);
  // Single-customer balance via the (tenant_id, customer_id) index —
  // replaces the find() over a tenant-wide loyaltyBalances array.
  const [customerLoyaltyBalance, setCustomerLoyaltyBalance] = useState(null);
  useEffect(() => {
    if (!customer?.id) { setCustomerLoyaltyTx([]); setCustomerLoyaltyBalance(null); return; }
    let cancelled = false;
    apiFetch(`/api/analytics/loyalty-customer-ledger?customerId=${encodeURIComponent(customer.id)}&limit=200`)
      .then(res => { if (!cancelled && res.ok) setCustomerLoyaltyTx(res.transactions || []); })
      .catch(() => { if (!cancelled) setCustomerLoyaltyTx([]); });
    apiFetch(`/api/analytics/loyalty-balance?customerId=${encodeURIComponent(customer.id)}`)
      .then(res => { if (!cancelled && res.ok) setCustomerLoyaltyBalance(res.balance || null); })
      .catch(() => { if (!cancelled) setCustomerLoyaltyBalance(null); });
    return () => { cancelled = true; };
  }, [customer?.id]);
  // Backward compat alias for places still using sqlStats
  const sqlStats = sqlDetail?.stats || null;
  const sqlLoading = customer?.id && !sqlDetail;
  const SqlLoadingPlaceholder = () => (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <div style={{ display: 'inline-block', width: 24, height: 24, border: '3px solid #e5e7eb', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const docFileRef = useRef(null);
  const [telegramLinked, setTelegramLinked] = useState(null); // null=loading, false, or {chatId, ...}
  const [botUsername, setBotUsername] = useState(null);
  const [botToken, setBotToken] = useState(null);
  const [tgMessage, setTgMessage] = useState('');
  const [tgSending, setTgSending] = useState(false);

  // Documents state
  const [showDocForm, setShowDocForm] = useState(false);
  const [docForm, setDocForm] = useState({ name: '', text: '', file: null });
  const [docNameError, setDocNameError] = useState(false);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [docToDelete, setDocToDelete] = useState(null);
  const [docUploading, setDocUploading] = useState(false);
  // Self-fetched customer_subscriptions for THIS customer (Phase A6).
  // Tenant-wide array no longer in DataContext.
  const [customerSubscriptions, setCustomerSubscriptions] = useState([]);
  useEffect(() => {
    if (!customer?.id) { setCustomerSubscriptions([]); return; }
    let cancelled = false;
    apiFetch(`/api/analytics/customer-subscriptions-index?customerId=${encodeURIComponent(customer.id)}`)
      .then(r => { if (!cancelled) setCustomerSubscriptions(r?.subscriptions || []); })
      .catch(() => { if (!cancelled) setCustomerSubscriptions([]); });
    return () => { cancelled = true; };
  }, [customer?.id]);

  // Self-fetched customer_documents for THIS customer's phone only —
  // replaces the tenant-wide DataContext.customerDocuments array.
  // Re-fetches whenever the phone changes (form edits) so docs from
  // a different record don't leak in when the same modal is reused.
  const [customerDocuments, setCustomerDocuments] = useState([]);
  const docsPhone = customer?.phone || form.phone || '';
  useEffect(() => {
    if (!docsPhone) { setCustomerDocuments([]); return; }
    let cancelled = false;
    apiFetch(`/api/analytics/customer-documents-index?customerPhone=${encodeURIComponent(docsPhone)}`)
      .then(r => { if (!cancelled) setCustomerDocuments(r?.documents || []); })
      .catch(() => { if (!cancelled) setCustomerDocuments([]); });
    return () => { cancelled = true; };
  }, [docsPhone]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Вкладка «Дзвінки» — лише коли заклад підключив телефонію.
  const [telephonyOn, setTelephonyOn] = useState(false);
  // Документ із шаблону (24.10): вікно створення і перегляд/друк .docx.
  const [tplModalOpen, setTplModalOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const customerSources = useCustomerSources();

  // Load Telegram link status for existing customer
  useEffect(() => {
    if (!customer?.id) { setTelegramLinked(false); return; }
    const loadTelegram = async () => {
      try {
        // Імʼя бота — з кешованого статусу каналів (не getMe на кожне
        // відкриття картки).
        try {
          const st = await getChannelsStatus();
          if (st?.telegram?.configured) {
            setBotToken(true);
            setBotUsername(st.telegram.username || null);
          }
          setTelephonyOn(!!st?.telephony?.configured);
        } catch { /* ignore */ }
        // Лише привʼязка — без завантаження всього треду.
        const res = await apiFetch(`/api/messages?customerId=${String(customer.id)}&linkOnly=1`);
        setTelegramLinked(res.link || false);
      } catch {
        setTelegramLinked(false);
      }
    };
    loadTelegram();
  }, [customer?.id]);

  // Посилання підписує сервер (аудит 2026-11-03): без підпису будь-хто, хто
  // знає id картки, привʼязував свій Telegram до чужої.
  const [telegramLink, setTelegramLink] = useState(null);
  useEffect(() => {
    setTelegramLink(null);
    if (!botUsername || !customer?.id || telegramLinked !== false) return undefined;
    let cancelled = false;
    apiFetch(`/api/telegram?action=invite-link&customerId=${encodeURIComponent(String(customer.id))}`)
      .then(r => { if (!cancelled) setTelegramLink(r?.url || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [botUsername, customer?.id, telegramLinked]);

  const [invitingVia, setInvitingVia] = useState(null);

  const sendInvite = async (channel) => {
    setInvitingVia(channel);
    try {
      await apiFetch('/api/telegram?action=send-invite', {
        method: 'POST',
        body: { customerId: customer.id, channel },
      });
      toast.success(t('custModal.inviteSent', 'Запрошення надіслано'));
    } catch (err) {
      toast.error(err?.body?.error || err?.message || t('custModal.inviteFailed', 'Не вдалося надіслати'));
    } finally {
      setInvitingVia(null);
    }
  };

  // Повний запис клієнта (24.10). Картку відкривають зі списку чи за
  // телефоном — там лише частина полів: перегляд показував порожні нотатки
  // й день народження, а збереження їх стирало. Дотягуємо документ цілком;
  // те, що людина вже встигла змінити, лишаємо її.
  const touchedRef = useRef(new Set());
  useEffect(() => {
    if (!customer?.id) return undefined;
    let cancelled = false;
    apiFetch(`/api/collection?name=customers&id=${encodeURIComponent(String(customer.id))}`)
      .then(r => {
        if (cancelled || !r?.data) return;
        setForm(prev => {
          const next = customerToForm(r.data);
          for (const k of touchedRef.current) next[k] = prev[k];
          return next;
        });
      })
      .catch(() => { /* лишаємо те, що є; сервер не зітре відсутні поля */ });
    return () => { cancelled = true; };
  }, [customer?.id]);

  const set = (field, value) => {
    touchedRef.current.add(field);
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = t('custModal.nameRequired');
    // Телефон обовʼязковий — крім карток, заведених із листування Instagram
    // чи Messenger (рішення власника 29.09): там його просто нема, і вимога
    // не дала б навіть дописати нотатку. Нова картка з форми — як і була;
    // нова картка з треду Instagram / Messenger (prefill) — теж без телефону.
    const fromSocial = ['instagram', 'messenger'].includes(customer?.source || prefill?.source);
    if (!form.phone.trim()) {
      if (!fromSocial) errs.phone = t('custModal.phoneRequired');
    } else {
      const phoneErr = validatePhone(form.phone);
      if (phoneErr) errs.phone = phoneErr;
    }
    if (form.additionalPhone) {
      const addPhoneErr = validatePhone(form.additionalPhone);
      if (addPhoneErr) errs.additionalPhone = addPhoneErr;
    }
    if (form.email) {
      const emailErr = validateEmail(form.email);
      if (emailErr) errs.email = emailErr;
    }
    if (form.instagram && !normalizeHandle(form.instagram)) {
      errs.instagram = t('custModal.instagramInvalid', 'Нік Instagram: латиниця, цифри, крапка, підкреслення');
    }
    if (form.discount < 0 || form.discount > 100) errs.discount = t('custModal.discountRange');

    // Duplicate checks: phone uniqueness is enforced server-side via
    // the customers_tenant_phone_uniq index — a violation there surfaces
    // as a clear error on save. Name duplicates are advisory only (two
    // customers can legitimately share a name), so no local check. The
    // old allCustomers.filter() pass was dropped with Phase 5c.

    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editMode) return;
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      setActiveTab('main');
      return;
    }
    setSaving(true);
    // Нік — у тому вигляді, в якому його звіряє сервер.
    // Поля instagram у формі може не бути (картка ще не дотягнула повний
    // запис) — тоді не шлемо його зовсім, щоб не стерти збережений нік.
    // Наявна картка — лише поля, які людина змінила (аудит 2026-11-03).
    // Форма — знімок на момент відкриття: email чи аватарку, які інтеграція
    // заповнила тим часом, збереження затирало порожнім значенням. Решту
    // сервер бере з бази (POST картки зливає з наявним документом).
    const source = customer?.id
      ? Object.fromEntries([...touchedRef.current].filter(k => k in form).map(k => [k, form[k]]))
      : form;
    const payload = 'instagram' in source
      ? { ...source, instagram: source.instagram ? normalizeHandle(source.instagram) : null }
      : { ...source };
    try { await onSave(payload); } finally { setSaving(false); }
  };

  const [avatarUploading, setAvatarUploading] = useState(false);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const customerId = customer?.id || `new_${Date.now()}`;
    const path = `tenants/${tenantId || 'default'}/avatars/customers/${customerId}_${Date.now()}.jpg`;
    setAvatarUploading(true);
    try {
      const url = await uploadImage(file, path);
      set('avatar', url);
    } catch (err) {
      console.error('Avatar upload failed:', err);
      toast.error(t('custModal.uploadError', 'Помилка завантаження фото'));
    } finally {
      setAvatarUploading(false);
    }
  };

  // «Джерело»: список закладу + поточне значення, якщо його з нього прибрали.
  const originOptions = form.origin && !customerSources.includes(form.origin)
    ? [...customerSources, form.origin] : customerSources;

  const days = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => String(currentYear - i));

  const tabs = [
    { id: 'main',      label: '👤 ' + t('custModal.tabMain') },
    { id: 'details',   label: '📋 ' + t('custModal.tabDetails') },
    { id: 'notes',     label: '📝 ' + t('custModal.tabNotes') },
    ...(customer ? (() => {
      const pd = (loyaltyPrograms || []).find(p => p.id === 'default');
      const hasAny = pd?.points?.enabled || pd?.tiers?.enabled || pd?.subscriptions?.enabled;
      return hasAny ? [{ id: 'loyalty', label: '⭐ ' + t('custModal.tabLoyalty') }] : [];
    })() : []),
    // Тільки для збереженого клієнта: задачу треба до чогось привʼязати.
    ...(customer && hasPermission('tasks_view')
      ? [{ id: 'tasks', label: '✅ ' + t('custModal.tabTasks', 'Задачі') }] : []),
    ...(customer && telephonyOn
      ? [{ id: 'calls', label: '📞 ' + t('custModal.tabCalls', 'Дзвінки') }] : []),
    { id: 'orders',    label: '📦 ' + t('custModal.tabOrders') },
    { id: 'visits',    label: '📅 ' + t('custModal.tabVisits') },
    { id: 'financial', label: '💰 ' + t('custModal.tabFinancial') },
    { id: 'documents', label: '📄 ' + t('custModal.tabDocuments') },
    ...(customer ? [{ id: 'analytics', label: '📊 ' + t('custModal.tabAnalytics') }] : []),
  ];

  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modal-header">
          <div className="modal-title-area">
            <h2>{!customer ? '➕ ' + t('custModal.newCustomer') : editMode ? '✏️ ' + t('custModal.editCustomer') : '📋 ' + (customer.name || t('custModal.viewCustomer'))}</h2>
            {customer && editMode && <span className="modal-subtitle">{customer.name}</span>}
          </div>
          <button className="modal-close-btn" onClick={onClose} title={t('custModal.closeEsc')}>✕</button>
        </div>

        <div className="modal-tabs" style={{ cursor: 'grab' }}
          onMouseDown={e => {
            const el = e.currentTarget;
            const startX = e.pageX - el.scrollLeft;
            el.style.cursor = 'grabbing';
            const onMove = (ev) => { el.scrollLeft = startX - ev.pageX; };
            const onUp = () => { el.style.cursor = 'grab'; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
          }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`modal-tab${activeTab === tab.id ? ' active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {!customer && prefill && (
          <div className="presence-banner">
            {prefill.fromCall
              ? t('custModal.fromCallHint', 'Картка з дзвінка: усі дзвінки з цього номера зʼявляться в її вкладці «Дзвінки».')
              : t('custModal.fromThreadHint', 'Картка з листування: після збереження тред привʼяжеться до неї разом з історією.')}
          </div>
        )}

        {isLocked && editingUser && (
          <div className="presence-banner">
            ✏️ {t('presence.editing')} <strong>{editingUser.displayName}</strong>
          </div>
        )}

        <form onSubmit={editMode ? handleSubmit : (e) => e.preventDefault()} className="modal-form">
          <div className="modal-body">

            {activeTab === 'main' && (
              <div className="tab-content">
                {!editMode && customer ? (
                  <>
                    <div className="view-profile-header">
                      <div className="avatar-preview-wrap">
                        {form.avatar ? (
                          <img src={form.avatar} alt={form.name} className="avatar-preview-img" />
                        ) : (
                          <div className="avatar-preview-initials">
                            {form.name ? getInitials(form.name) : '👤'}
                          </div>
                        )}
                      </div>
                      <div className="view-profile-info">
                        <h3 className="view-profile-name">{form.name || '—'}</h3>
                        {form.gender && form.gender !== 'other' && (
                          <span className="view-profile-gender">{GENDER_KEYS.find(g => g.value === form.gender) ? t(GENDER_KEYS.find(g => g.value === form.gender).key) : ''}</span>
                        )}
                        {/* «Звʼязатися пізніше»: задача на дату з привʼязкою до клієнта (21.10) */}
                        {hasPermission('tasks_view') && hasPermission('tasks_create') && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm view-profile-contact-later"
                            onClick={() => { setActiveTab('tasks'); setContactLaterSignal(Date.now()); }}
                          >
                            📞 {t('custModal.contactLater', 'Звʼязатися пізніше')}
                          </button>
                        )}
                      </div>
                    </div>

                    <SectionTitle>{t('custModal.personalData')}</SectionTitle>
                    <div className="view-fields">
                      <div className="view-field">
                        <span className="view-field-icon">📱</span>
                        <span className="view-field-value">{form.phone || '—'}</span>
                      </div>
                      {form.additionalPhone && (
                        <div className="view-field">
                          <span className="view-field-icon">📞</span>
                          <span className="view-field-value">{form.additionalPhone}</span>
                        </div>
                      )}
                      {form.email && (
                        <div className="view-field">
                          <span className="view-field-icon">✉️</span>
                          <span className="view-field-value">{form.email}</span>
                        </div>
                      )}
                      {form.instagram && (
                        <div className="view-field">
                          <span className="view-field-icon">📷</span>
                          <a className="view-field-value" href={`https://instagram.com/${form.instagram}`}
                            target="_blank" rel="noopener noreferrer">@{form.instagram}</a>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="avatar-upload-section">
                      <div className="avatar-preview-wrap">
                        {form.avatar ? (
                          <img src={form.avatar} alt={t('custModal.avatar')} className="avatar-preview-img" />
                        ) : (
                          <div className="avatar-preview-initials">
                            {form.name ? getInitials(form.name) : '👤'}
                          </div>
                        )}
                        <button type="button" className="avatar-overlay-btn"
                          onClick={() => fileRef.current?.click()} title={t('custModal.changePhoto')}>
                          📷
                        </button>
                      </div>
                      <div className="avatar-controls">
                        <p className="avatar-hint">{t('custModal.profilePhoto')}</p>
                        <div className="avatar-btns">
                          <button type="button" className="btn btn-outline btn-sm"
                            onClick={() => fileRef.current?.click()}
                            disabled={avatarUploading}>
                            {avatarUploading ? '⏳' : '📷'} {t('custModal.uploadPhoto')}
                          </button>
                          {form.avatar && (
                            <button type="button" className="btn btn-ghost btn-sm"
                              onClick={() => set('avatar', null)}>
                              🗑 {t('custModal.deletePhoto')}
                            </button>
                          )}
                        </div>
                        <input ref={fileRef} type="file" accept="image/*"
                          style={{ display: 'none' }} onChange={handleAvatarUpload} />
                      </div>
                    </div>

                    <SectionTitle>{t('custModal.personalData')}</SectionTitle>
                    <div className="form-grid-2">
                      <FormField label={t('custModal.fullName') + ' *'} error={errors.name} full>
                        <input type="text" value={form.name}
                          onChange={e => set('name', e.target.value)}
                          placeholder={t('custModal.fullNamePlaceholder')}
                          className={errors.name ? 'input-error' : ''} />
                      </FormField>
                      <FormField label={t('custModal.mobilePhone') + ' *'} error={errors.phone}>
                        <input type="tel" value={form.phone}
                          onChange={e => set('phone', formatPhone(e.target.value))}
                          placeholder="+38 050 123 45 67"
                          className={errors.phone ? 'input-error' : ''} />
                      </FormField>
                      <FormField label={t('custModal.additionalPhone')} error={errors.additionalPhone}>
                        <input type="tel" value={form.additionalPhone || ''}
                          onChange={e => set('additionalPhone', formatPhone(e.target.value))}
                          placeholder="+38 067 123 45 67"
                          className={errors.additionalPhone ? 'input-error' : ''} />
                      </FormField>
                      <FormField label="E-mail" error={errors.email} full>
                        <input type="email" value={form.email}
                          onChange={e => set('email', e.target.value)}
                          placeholder="email@example.com"
                          className={errors.email ? 'input-error' : ''} />
                      </FormField>
                      {/* Нік Instagram: за ним Direct цієї людини сам лягає на картку. */}
                      <FormField label={t('custModal.instagram', 'Instagram (нік)')} error={errors.instagram} full>
                        <input type="text" value={form.instagram || ''}
                          onChange={e => set('instagram', e.target.value)}
                          placeholder="@olena_fit"
                          autoCapitalize="off" autoCorrect="off" spellCheck={false}
                          className={errors.instagram ? 'input-error' : ''} />
                      </FormField>
                    </div>

                    <SectionTitle>{t('custModal.gender')}</SectionTitle>
                    <div className="radio-group">
                      {GENDER_KEYS.map(g => (
                        <label key={g.value} className={`radio-card${form.gender === g.value ? ' selected' : ''}`}>
                          <input type="radio" name="gender" value={g.value}
                            checked={form.gender === g.value}
                            onChange={() => set('gender', g.value)} />
                          <span>{t(g.key)}</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'details' && (
              <div className="tab-content">
                {!editMode && customer ? (
                  <>
                    <SectionTitle>{t('custModal.categoryAndClassification')}</SectionTitle>
                    <div className="view-fields">
                      {form.category && (
                        <div className="view-field">
                          <span className="view-field-label">{t('custModal.category')}</span>
                          <span className="view-field-value">{(categories || []).find(c => c.name === form.category)?.icon || ''} {form.category}</span>
                        </div>
                      )}
                      {form.importance && (
                        <div className="view-field">
                          <span className="view-field-label">{t('custModal.importanceLabel')}</span>
                          <span className="view-field-value">{form.importance}</span>
                        </div>
                      )}
                      {form.discount > 0 && (
                        <div className="view-field">
                          <span className="view-field-label">{t('custModal.discount')}</span>
                          <span className="view-field-value">-{form.discount}%</span>
                        </div>
                      )}
                      {form.cardNumber && (
                        <div className="view-field">
                          <span className="view-field-label">{t('custModal.cardNumber')}</span>
                          <span className="view-field-value">{form.cardNumber}</span>
                        </div>
                      )}
                      {(form.origin || form.originAuto) && (
                        <div className="view-field">
                          <span className="view-field-label">{t('custModal.origin', 'Джерело')}</span>
                          <span className="view-field-value">{form.origin || form.originAuto}</span>
                        </div>
                      )}
                    </div>

                    {(form.birthDay || form.birthMonth || form.birthYear) && (
                      <>
                        <SectionTitle>{t('custModal.birthDate')}</SectionTitle>
                        <div className="view-fields">
                          <div className="view-field">
                            <span className="view-field-icon">🎂</span>
                            <span className="view-field-value">
                              {form.birthDay || '—'}.{form.birthMonth || '—'}.{form.birthYear || '—'}
                            </span>
                          </div>
                        </div>
                      </>
                    )}

                    {form.forbidOnlineBooking && (
                      <>
                        <SectionTitle>{t('custModal.restrictions')}</SectionTitle>
                        <div className="view-fields">
                          <div className="view-field">
                            <span className="view-field-icon">🚫</span>
                            <span className="view-field-value">{t('custModal.forbidOnlineBooking')}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <SectionTitle>{t('custModal.categoryAndClassification')}</SectionTitle>
                    <div className="form-grid-2">
                      <FormField label={t('custModal.category')}>
                        <select value={form.category} onChange={e => set('category', e.target.value)}>
                          {(categories || []).map(c => <option key={c.id} value={c.name}>{c.icon} {c.name}</option>)}
                        </select>
                      </FormField>
                      <FormField label={t('custModal.importanceLabel')}>
                        <select value={form.importance} onChange={e => set('importance', e.target.value)}>
                          {(importanceLevels || []).map(imp => <option key={imp.id} value={imp.name}>{imp.name}</option>)}
                        </select>
                      </FormField>
                      <FormField label={t('custModal.discount')} error={errors.discount}>
                        <input type="number" inputMode="numeric" value={form.discount} min="0" max="100"
                          onChange={e => set('discount', Math.min(100, Math.max(0, Number(e.target.value))))}
                          className={errors.discount ? 'input-error' : ''} />
                      </FormField>
                      <FormField label={t('custModal.cardNumber')}>
                        <input type="text" value={form.cardNumber}
                          onChange={e => set('cardNumber', e.target.value)}
                          placeholder="UA-000-2024" />
                      </FormField>
                      {/* Звідки клієнт прийшов — список закладу (Клієнти → Категорії → Джерела). */}
                      <FormField label={t('custModal.origin', 'Джерело')}>
                        <select value={form.origin || ''} onChange={e => set('origin', e.target.value)}>
                          <option value="">
                            {form.originAuto
                              ? `${form.originAuto} ${t('custModal.originAuto', '(визначено автоматично)')}`
                              : t('custModal.originNone', '— не вказано —')}
                          </option>
                          {originOptions.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </FormField>
                    </div>

                    <SectionTitle>{t('custModal.birthDate')}</SectionTitle>
                    <div className="form-grid-3">
                      <FormField label={t('custModal.day')}>
                        <select value={form.birthDay} onChange={e => set('birthDay', e.target.value)}>
                          <option value="">—</option>
                          {days.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                      </FormField>
                      <FormField label={t('custModal.month')}>
                        <select value={form.birthMonth} onChange={e => set('birthMonth', e.target.value)}>
                          <option value="">—</option>
                          {MONTH_KEYS.map(m => <option key={m.value} value={m.value}>{t(m.key)}</option>)}
                        </select>
                      </FormField>
                      <FormField label={t('custModal.year')}>
                        <select value={form.birthYear} onChange={e => set('birthYear', e.target.value)}>
                          <option value="">—</option>
                          {years.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                      </FormField>
                    </div>

                    <SectionTitle>{t('custModal.referral')}</SectionTitle>
                    <div className="form-field">
                      <label className="field-label">{t('custModal.referredBy')}</label>
                      <ReferralSearch
                        excludeId={customer?.id}
                        value={form.referredBy || ''}
                        onChange={id => set('referredBy', id)}
                      />
                      <small style={{ color: 'var(--text-muted)' }}>{t('custModal.referredByHint')}</small>
                    </div>

                    <SectionTitle>{t('custModal.restrictions')}</SectionTitle>
                    <label className="checkbox-toggle">
                      <input type="checkbox" checked={form.forbidOnlineBooking}
                        onChange={e => set('forbidOnlineBooking', e.target.checked)} />
                      <div className="toggle-switch"></div>
                      <div className="toggle-label">
                        <span className="toggle-title">🚫 {t('custModal.forbidOnlineBooking')}</span>
                        <span className="toggle-desc">{t('custModal.forbidOnlineBookingDesc')}</span>
                      </div>
                    </label>

                    {customer && (
                      <>
                        <SectionTitle>{t('custModal.telegramReminders')}</SectionTitle>
                    {telegramLinked === null ? (
                      <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>{t('custModal.loading')}</p>
                    ) : telegramLinked ? (
                      <>
                        <div className="telegram-status-linked">
                          <span className="telegram-status-icon">✅</span>
                          <div>
                            <span className="telegram-status-text">{t('custModal.telegramConnected')}</span>
                            {telegramLinked.telegramUsername && (
                              <span className="telegram-status-username">@{telegramLinked.telegramUsername}</span>
                            )}
                          </div>
                        </div>
                        <div className="telegram-send-section">
                          <textarea
                            className="telegram-send-input"
                            rows="3"
                            value={tgMessage}
                            onChange={e => setTgMessage(e.target.value)}
                            placeholder={t('custModal.writeMessagePlaceholder')}
                          />
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={tgSending || !tgMessage.trim()}
                            onClick={async () => {
                              setTgSending(true);
                              try {
                                // customerId → сервер запише повідомлення в історію
                                // «Комунікації». Доти надіслане з картки там не зʼявлялось.
                                const data = await apiFetch('/api/telegram?action=send', {
                                  method: 'POST',
                                  body: {
                                    chatId: telegramLinked.chatId,
                                    text: tgMessage.trim(),
                                    customerId: customer?.id || null,
                                  },
                                });
                                if (data.ok) {
                                  setTgMessage('');
                                  toast.success(t('custModal.messageSent', 'Надіслано'));
                                } else {
                                  toast.error(t('custModal.error') + ': ' + (data.error || t('custModal.unknown')));
                                }
                              } catch (err) {
                                toast.error(t('custModal.error') + ': ' + err.message);
                              }
                              setTgSending(false);
                            }}
                          >
                            {tgSending ? t('custModal.sending') : t('custModal.send')}
                          </button>
                        </div>
                      </>
                    ) : telegramLink ? (
                      <div className="telegram-link-section">
                        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 8 }}>
                          {t('custModal.sendLinkToCustomer')}
                        </p>
                        <div className="telegram-link-box">
                          <input type="text" value={telegramLink} readOnly className="telegram-link-input" />
                          <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            onClick={() => {
                              navigator.clipboard.writeText(telegramLink);
                            }}
                          >
                            {t('custModal.copy')}
                          </button>
                        </div>
                        {/* Скопійоване посилання ще треба кудись вставити.
                            Кнопки шлють його тим каналом, який у клієнта вже
                            є, — саме через відсутність доставки в тенанта з
                            робочим ботом було нуль підписників. */}
                        <div className="telegram-invite-actions">
                          {(customer?.phone || form.phone) && (
                            <button
                              type="button" className="btn btn-secondary btn-sm" disabled={invitingVia}
                              onClick={() => sendInvite('sms')}
                            >
                              {invitingVia === 'sms' ? '…' : t('custModal.sendInviteSms', 'Надіслати SMS')}
                            </button>
                          )}
                          {(customer?.email || form.email) && (
                            <button
                              type="button" className="btn btn-secondary btn-sm" disabled={invitingVia}
                              onClick={() => sendInvite('email')}
                            >
                              {invitingVia === 'email' ? '…' : t('custModal.sendInviteEmail', 'Надіслати Email')}
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                        {t('custModal.setupTelegramBot')}
                      </p>
                    )}
                  </>
                )}
                  </>
                )}
              </div>
            )}

            {activeTab === 'notes' && (
              <div className="tab-content">
                <SectionTitle>{t('custModal.note')}</SectionTitle>
                {!editMode && customer ? (
                  <div className="view-fields">
                    <div className="view-field" style={{ minHeight: 60, alignItems: 'flex-start' }}>
                      <span className="view-field-value" style={{ whiteSpace: 'pre-wrap' }}>{form.note || '—'}</span>
                    </div>
                  </div>
                ) : (
                  <FormField full>
                    <textarea value={form.note} rows="4"
                      onChange={e => set('note', e.target.value)}
                      placeholder={t('custModal.notePlaceholder')} />
                  </FormField>
                )}
                <SectionTitle>{t('custModal.comments')}</SectionTitle>
                {/* Журнал: зберігається одразу й окремо від форми (13.10). */}
                <CustomerComments customerId={customer?.id || null} legacyText={customer?.comments || ''} />
              </div>
            )}

            {activeTab === 'visits' && (() => {
              const phone = customer?.phone || form.phone;
              const STATUSES = {
                waiting:   { label: t('custModal.statusWaiting'),    color: '#f59e0b', icon: '⏳' },
                confirmed: { label: t('custModal.statusConfirmed'),  color: '#22c55e', icon: '✓' },
                arrived:   { label: t('custModal.statusArrived'),    color: '#0ea5e9', icon: '●' },
                no_show:   { label: t('custModal.statusNoShow'),     color: '#ef4444', icon: '✕' },
              };
              const METHODS = { cash: t('custModal.methodCash'), card: t('custModal.methodCard'), terminal: t('custModal.methodCard'), transfer: t('custModal.methodTransfer'), online: t('custModal.methodOnline'), subscription: t('custModal.methodSubscription') };

              if (sqlLoading) return <SqlLoadingPlaceholder />;
              const allVisits = (sqlDetail?.appointments || [])
                .sort((a, b) => (b.date + b.startTime).localeCompare(a.date + a.startTime));

              const totalConfirmed = allVisits.filter(a => a.status === 'confirmed').length;
              const totalNoShow = allVisits.filter(a => a.status === 'no_show').length;

              // Subscription visit price lookup (same as Financial tab)
              const visitCustId = String(customer?.id || '');
              const visitSubPriceMap = {};
              (customerSubscriptions || []).forEach(sub => {
                if (String(sub.customerId) !== visitCustId) return;
                const plan = (subscriptionPlans || []).find(p => String(p.id) === String(sub.planId));
                if (plan?.price > 0 && plan?.totalVisits > 0) {
                  visitSubPriceMap[sub.id] = Math.round(Number(plan.price) / Number(plan.totalVisits) * 100) / 100;
                }
              });
              const getVisitPrice = (a) => {
                if (a.paymentMethod === 'subscription') {
                  return a.subscriptionVisitPrice > 0
                    ? Number(a.subscriptionVisitPrice)
                    : (a.subscriptionId && visitSubPriceMap[a.subscriptionId]) || 0;
                }
                return Number(a.servicePrice || 0) * (1 - (Number(a.discount) || 0) / 100);
              };

              const totalSpent = allVisits.reduce((sum, a) => {
                if (a.paid) return sum + getVisitPrice(a);
                return sum;
              }, 0);

              const fmtDate = (dateStr) => {
                if (!dateStr) return '—';
                const [y, mo, d] = dateStr.split('-');
                const months = ['',t('custModal.monthAbbrJan'),t('custModal.monthAbbrFeb'),t('custModal.monthAbbrMar'),t('custModal.monthAbbrApr'),t('custModal.monthAbbrMay'),t('custModal.monthAbbrJun'),t('custModal.monthAbbrJul'),t('custModal.monthAbbrAug'),t('custModal.monthAbbrSep'),t('custModal.monthAbbrOct'),t('custModal.monthAbbrNov'),t('custModal.monthAbbrDec')];
                return `${Number(d)} ${months[Number(mo)]} ${y}`;
              };

              const refundMap = {};
              (sqlDetail?.transactions || []).forEach(t => {
                if (t.type === 'refund' && t.appointmentId) refundMap[t.appointmentId] = t;
              });

              return (
                <div className="tab-content">
                  <SectionTitle>{t('custModal.visitHistory')}</SectionTitle>

                  {allVisits.length > 0 ? (
                    <>
                      <div className="visits-stats">
                        <div className="visits-stat-chip">
                          <span className="visits-stat-num">{allVisits.length}</span>
                          <span className="visits-stat-label">{t('custModal.total')}</span>
                        </div>
                        <div className="visits-stat-chip" style={{ '--stat-color': '#22c55e' }}>
                          <span className="visits-stat-num">{totalConfirmed}</span>
                          <span className="visits-stat-label">{t('custModal.completed')}</span>
                        </div>
                        {totalNoShow > 0 && (
                          <div className="visits-stat-chip" style={{ '--stat-color': '#ef4444' }}>
                            <span className="visits-stat-num">{totalNoShow}</span>
                            <span className="visits-stat-label">{t('custModal.noShow')}</span>
                          </div>
                        )}
                        <div className="visits-stat-chip" style={{ '--stat-color': '#6366f1' }}>
                          <span className="visits-stat-num">{totalSpent.toLocaleString('uk-UA')} ₴</span>
                          <span className="visits-stat-label">{t('custModal.paidLabel')}</span>
                        </div>
                      </div>

                      <div className="timeline">
                        {allVisits.map(a => {
                          const aptStaff = (staff || []).find(s => s.id === a.staffId);
                          const st = STATUSES[a.status] || { label: a.status, color: '#94a3b8', icon: '●' };
                          const total = getVisitPrice(a);
                          const refund = refundMap[a.id];
                          const isNoShow = a.status === 'no_show';

                          return (
                            <div key={a.id} className={`timeline-item${isNoShow ? ' timeline-item--noshow' : ''}`}>
                              <div className="timeline-dot" style={{ '--dot-color': st.color }}>
                                <span>{st.icon}</span>
                              </div>
                              <div className="timeline-card timeline-card--clickable" onClick={() => onOpenAppointment?.(a)}>
                                <div className="timeline-header">
                                  <span className="timeline-date">{fmtDate(a.date)}</span>
                                  <span className="timeline-time">{a.startTime || ''}{a.endTime ? ` – ${a.endTime}` : ''}</span>
                                  <span className="timeline-status" style={{ '--status-color': st.color }}>{st.label}</span>
                                </div>
                                <div className="timeline-service">{a.serviceName || t('custModal.service')}</div>
                                {aptStaff && (
                                  <div className="timeline-staff">
                                    <span className="timeline-staff-dot" style={{ background: aptStaff.color || '#6366f1' }} />
                                    {aptStaff.name}
                                  </div>
                                )}
                                {(a.paid || total > 0) && !isNoShow && (
                                  <div className="timeline-payment">
                                    <span className={`timeline-amount${a.paid ? ' timeline-amount--paid' : ''}`}>
                                      {total.toLocaleString('uk-UA')} ₴
                                    </span>
                                    {a.paid && (
                                      <span className="timeline-method">
                                        {METHODS[a.paymentMethod] || a.paymentMethod || ''} ✓
                                      </span>
                                    )}
                                    {refund && (
                                      <span className="timeline-refund">↩ {t('custModal.refund')}</span>
                                    )}
                                  </div>
                                )}
                                {a.note && (
                                  <div className="timeline-note">📝 {a.note}</div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <p style={{ color: 'var(--text-muted)', marginTop: 8, fontSize: 13 }}>{t('custModal.noVisitsYet')}</p>
                  )}
                </div>
              );
            })()}

            {/* Монтується лише коли вкладку відкрили — тоді ж і робить свій
                запит. Картку клієнта відкривають переважно «глянути телефон». */}
            {activeTab === 'tasks' && customer && (
              <CustomerTasksTab customer={customer} activeLocationId={activeLocationId} contactLaterSignal={contactLaterSignal} />
            )}

            {activeTab === 'calls' && customer && (
              <CustomerCallsTab customerId={customer.id} phone={customer.phone} />
            )}

            {activeTab === 'orders' && (() => {
              const phone = customer?.phone || form.phone;
              const custId = customer?.id;
              const STATUS_COLORS = {
                new: '#6b7280', confirmed: '#3b82f6', packed: '#f59e0b',
                shipped: '#8b5cf6', delivered: '#22c55e', cancelled: '#ef4444',
                pos_completed: '#22c55e',
              };
              const STATUS_KEYS = {
                new: 'custModal.orderStatusNew', confirmed: 'custModal.orderStatusConfirmed',
                packed: 'custModal.orderStatusPacked', shipped: 'custModal.orderStatusShipped',
                delivered: 'custModal.orderStatusDelivered', cancelled: 'custModal.orderStatusCancelled',
                pos_completed: 'custModal.posCompleted',
              };
              const PM_KEYS = {
                cash: 'custModal.cash', terminal: 'custModal.terminal',
                transfer: 'custModal.transfer', online: 'custModal.online',
              };

              if (sqlLoading) return <SqlLoadingPlaceholder />;
              const customerOrders = (sqlDetail?.orders || [])
                .map(o => ({
                  ...o, _source: 'order',
                  paymentStatus: o.paymentStatus || (o.refunded ? 'refunded' : o.paid ? 'paid' : 'unpaid'),
                }));

              // POS sales: product_sale transactions NOT linked to orders or appointments
              // (those are already counted in Orders section or as service materials)
              const posTx = (sqlDetail?.transactions || [])
                .filter(t => t.type === 'product_sale' && (t.source === 'pos' || t.batchId) && !t.orderId && !t.appointmentId);
              // Group by batchId — returns (negative) reduce the batch total
              const posBatches = {};
              posTx.forEach(tx => {
                const key = tx.batchId || tx.id;
                if (!posBatches[key]) posBatches[key] = {
                  id: `pos_${key}`, createdAt: tx.createdAt, items: [], totalAmount: 0,
                  method: tx.method || tx.paymentMethod, _source: 'pos',
                  paymentMethod: tx.method || tx.paymentMethod,
                };
                posBatches[key].items.push({
                  productName: tx.productName || tx.description?.replace('POS: ', '') || tx.serviceName || '—',
                  quantity: tx.quantity || 1,
                });
                posBatches[key].totalAmount += tx.amount;
                if (tx.createdAt < posBatches[key].createdAt) posBatches[key].createdAt = tx.createdAt;
              });
              const posSales = Object.values(posBatches).map(b => ({
                ...b,
                status: b.totalAmount <= 0 ? 'cancelled' : 'pos_completed',
                paymentStatus: b.totalAmount <= 0 ? 'refunded' : 'paid',
              }));

              // Merge and sort
              const allPurchases = [...customerOrders, ...posSales].sort((a, b) => b.createdAt - a.createdAt);

              const totalOrdersCount = customerOrders.filter(o => o.status !== 'cancelled' && o.paymentStatus !== 'refunded').length;
              const deliveredOrders = customerOrders.filter(o => o.status === 'delivered' && o.paymentStatus !== 'refunded').length;
              const posSalesCount = posSales.filter(s => s.status !== 'cancelled').length;
              const totalSpent = allPurchases.reduce((s, p) => {
                if (p._source === 'order') {
                  // Orders: exclude cancelled (never fulfilled) and refunded (returned)
                  if (p.status === 'cancelled' || p.paymentStatus === 'refunded') return s;
                  return s + (p.totalAmount || 0);
                }
                // POS: include ALL — returns have negative amounts that naturally reduce total
                return s + (p.totalAmount || 0);
              }, 0);

              const fmtDate = (ts) => {
                const d = new Date(ts);
                return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
              };

              return (
                <div className="tab-content">
                  <SectionTitle>{t('custModal.orderHistory')}</SectionTitle>

                  {allPurchases.length > 0 ? (
                    <>
                      <div className="visits-stats">
                        <div className="visits-stat-chip">
                          <span className="visits-stat-num">{totalOrdersCount}</span>
                          <span className="visits-stat-label">{t('custModal.ordersTotal')}</span>
                        </div>
                        <div className="visits-stat-chip" style={{ '--stat-color': '#22c55e' }}>
                          <span className="visits-stat-num">{deliveredOrders}</span>
                          <span className="visits-stat-label">{t('custModal.ordersDelivered')}</span>
                        </div>
                        <div className="visits-stat-chip" style={{ '--stat-color': '#8b5cf6' }}>
                          <span className="visits-stat-num">{posSalesCount}</span>
                          <span className="visits-stat-label">{t('custModal.posSalesCount')}</span>
                        </div>
                        <div className="visits-stat-chip" style={{ '--stat-color': '#6366f1' }}>
                          <span className="visits-stat-num">{totalSpent.toLocaleString('uk-UA')} ₴</span>
                          <span className="visits-stat-label">{t('custModal.ordersPaid')}</span>
                        </div>
                      </div>

                      <div className="timeline">
                        {allPurchases.map(o => {
                          const stColor = STATUS_COLORS[o.status] || '#94a3b8';
                          const stLabel = STATUS_KEYS[o.status] ? t(STATUS_KEYS[o.status]) : o.status;
                          const pmLabel = PM_KEYS[o.paymentMethod] ? t(PM_KEYS[o.paymentMethod]) : '';
                          const isPaid = o.paymentStatus === 'paid';
                          const isRefunded = o.paymentStatus === 'refunded';
                          const isPos = o._source === 'pos';
                          const label = isPos
                            ? o.items.map(i => i.productName).join(', ')
                            : (o.items || []).map(item => item.productName || '').filter(Boolean).join(', ') || '—';
                          const orderNum = isPos ? '' : `#${String(o.orderNumber || o.id).padStart(3, '0')}`;
                          const sourceLabel = isPos ? t('custModal.posSourceLabel') : t('custModal.orderSourceLabel');

                          const isReturned = isRefunded || o.returnStatus === 'returned';

                          return (
                            <div key={o.id} className={`timeline-item${o.status === 'cancelled' || isReturned ? ' timeline-item--noshow' : ''}`}>
                              <div className="timeline-dot" style={{ '--dot-color': isReturned ? '#ef4444' : stColor }}>
                                <span>{isReturned ? '✕' : o.status === 'delivered' || o.status === 'pos_completed' ? '✓' : o.status === 'cancelled' ? '✕' : '●'}</span>
                              </div>
                              <div className="timeline-card">
                                <div className="timeline-header">
                                  <span className="timeline-date">{fmtDate(o.createdAt)}</span>
                                  {orderNum && <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{orderNum}</span>}
                                  <span className="timeline-source-badge" style={{ background: isPos ? '#f0fdf4' : '#eff6ff', color: isPos ? '#16a34a' : '#2563eb', padding: '2px 8px', borderRadius: 6, fontSize: '0.7rem', fontWeight: 600 }}>{sourceLabel}</span>
                                  <span className="timeline-status" style={{ '--status-color': stColor }}>{stLabel}</span>
                                </div>
                                <div className="timeline-service">{label}</div>
                                <div className="timeline-payment">
                                  <span className={`timeline-amount${isPaid ? ' timeline-amount--paid' : ''}`}>
                                    {(o.totalAmount || 0).toLocaleString('uk-UA')} ₴
                                  </span>
                                  {isPaid && pmLabel && (
                                    <span className="timeline-method">{pmLabel} ✓</span>
                                  )}
                                  {isRefunded && (
                                    <span className="timeline-refund">↩ {t('custModal.refund')}</span>
                                  )}
                                </div>
                                {o.note && (
                                  <div className="timeline-note">📝 {o.note}</div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <p style={{ color: 'var(--text-muted)', marginTop: 8, fontSize: 13 }}>{t('custModal.noOrdersYet')}</p>
                  )}
                </div>
              );
            })()}

            {activeTab === 'financial' && (() => {
              const phone = customer?.phone || form.phone;

              if (sqlLoading) return <SqlLoadingPlaceholder />;

              const custId = String(customer?.id || '');
              const customerTx = sqlDetail?.transactions || [];
              const servicePaymentTx = customerTx.filter(t => t.type === 'payment');
              const productPaymentTx = customerTx.filter(t => t.type === 'product_sale');
              // Service-level refunds: type=refund NOT in subscription category, linked to an appointment
              const serviceRefundTx = customerTx.filter(t =>
                t.type === 'refund' && t.category !== 'subscription_refund' && t.appointmentId
              );

              const allApts = (sqlDetail?.appointments || [])
                .slice()
                .sort((a, b) => (a.date + (a.startTime || '')).localeCompare(b.date + (b.startTime || '')));

              // Build subscription visit price lookup (plan.price / plan.totalVisits)
              const subVisitPriceMap = {};
              (customerSubscriptions || []).forEach(sub => {
                if (String(sub.customerId) !== custId && sub.customerId !== phone) return;
                const plan = (subscriptionPlans || []).find(p => String(p.id) === String(sub.planId));
                if (plan?.price > 0 && plan?.totalVisits > 0) {
                  subVisitPriceMap[sub.id] = Math.round(Number(plan.price) / Number(plan.totalVisits) * 100) / 100;
                }
              });

              // Build all service rows (confirmed + non-confirmed with net_paid > 0).
              // Non-confirmed rows display '—' in charged column (service not delivered).
              // Rows where payment was fully cancelled by refund (net=0) are hidden as noise.
              const serviceRows = allApts
                .map(a => {
                  const isSubPaid = a.paymentMethod === 'subscription';
                  let charged;
                  if (isSubPaid) {
                    charged = a.subscriptionVisitPrice > 0
                      ? Number(a.subscriptionVisitPrice)
                      : (a.subscriptionId && subVisitPriceMap[a.subscriptionId]) || 0;
                  } else {
                    charged = Number(a.servicePrice || 0) * (1 - (Number(a.discount || 0) / 100));
                  }
                  const payments = servicePaymentTx.filter(t => String(t.appointmentId) === String(a.id));
                  const refunds = serviceRefundTx.filter(t => String(t.appointmentId) === String(a.id));
                  const paidSum = payments.reduce((s, t) => s + (t.amount || 0), 0);
                  const refundSum = refunds.reduce((s, t) => s + Math.abs(t.amount || 0), 0);
                  const netPaid = paidSum - refundSum;
                  return {
                    appointment: a,
                    charged,
                    isConfirmed: a.status === 'confirmed',
                    payment: payments[0] || null, // for method icon
                    netPaid,
                  };
                })
                .filter(r => r.isConfirmed || r.netPaid > 0);

              // ── Product charges: outflow stockMovements (SQL-backed) ──
              // SQL endpoint already returns only movements linked to this customer
              // via appointment/batch/order joins. Fallback to local filtering.
              const MU_OUTFLOW_TYPES = new Set(['sale', 'order', 'appointment']);
              const allOutflows = (sqlDetail?.stockMovements || []).filter(m =>
                MU_OUTFLOW_TYPES.has(m.type) || (m.type === 'writeoff' && m.appointmentId)
              );

              // Cancel/return movements for netto calculation (SQL-backed)
              const cancelMoves = (sqlDetail?.stockMovements || []).filter(m =>
                m.type === 'sale_cancel' || m.type === 'order_cancel' || m.type === 'appointment_cancel' || m.type === 'return'
              );
              const returnedQty = {};
              cancelMoves.forEach(m => {
                let groupKey = '';
                if (m.type === 'sale_cancel' && m.originalBatchId) groupKey = `sale_${m.originalBatchId}`;
                else if (m.type === 'order_cancel' && m.orderId) groupKey = `order_${m.orderId}`;
                else if (m.type === 'return' && m.orderId) groupKey = `order_${m.orderId}`;
                else if (m.type === 'appointment_cancel' && m.appointmentId) groupKey = `apt_${m.appointmentId}`;
                if (!groupKey) return;
                const key = `${groupKey}_${m.productId}`;
                returnedQty[key] = (returnedQty[key] || 0) + (m.quantity || 0);
              });

              // Resolve customer phone from movement.
              // When using SQL data, movements are already pre-filtered for this customer
              // via JOIN — so we can trust they belong to this customer.
              const resolvePhone = (m) => {
                // SQL data is pre-filtered for this customer via JOINs
                if (m.appointmentId) {
                  const apt = (sqlDetail?.appointments || []).find(a => String(a.id) === String(m.appointmentId));
                  if (apt?.clientPhone) return apt.clientPhone;
                }
                const tx = customerTx.find(t => {
                  if (t.type !== 'product_sale' || !t.customerPhone) return false;
                  if (m.batchId && t.batchId) return t.batchId === m.batchId;
                  if (m.orderId && t.orderId) return String(t.orderId) === String(m.orderId);
                  return false;
                });
                return tx?.customerPhone || '';
              };

              // Get charge price per unit
              const getUnitCharge = (m) => {
                if (m.unitPrice) return m.unitPrice * (1 - (m.discount || 0) / 100);
                if (m.type === 'appointment' && m.appointmentId) {
                  const tx = customerTx.find(t =>
                    t.type === 'product_sale' && String(t.appointmentId) === String(m.appointmentId)
                    && String(t.productId) === String(m.productId) && t.amount > 0
                  );
                  if (tx) return tx.amount / (tx.quantity || 1);
                }
                const prod = (products || []).find(p => String(p.id) === String(m.productId));
                return prod?.sellPrice || 0;
              };

              // Group outflows by event
              const productGroupsMap = {};
              allOutflows.forEach(m => {
                if (resolvePhone(m) !== phone) return;
                const key = m.type === 'sale' ? `sale_${m.batchId || m.id}`
                  : m.type === 'order' ? `order_${m.orderId || m.id}`
                  : `apt_${m.appointmentId || m.id}`;
                if (!productGroupsMap[key]) {
                  productGroupsMap[key] = { key, type: m.type, createdAt: m.createdAt, movements: [], totalCharged: 0 };
                }
                const returnKey = `${key}_${m.productId}`;
                const returned = returnedQty[returnKey] || 0;
                const nettoQty = Math.max(0, (m.quantity || 0) - returned);
                if (nettoQty > 0) {
                  const unitCharge = getUnitCharge(m);
                  productGroupsMap[key].movements.push({ ...m, nettoQty });
                  productGroupsMap[key].totalCharged += unitCharge * nettoQty;
                }
              });
              const productGroups = Object.values(productGroupsMap)
                .filter(g => g.movements.length > 0)
                .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))
                .map(g => {
                  let payments = [];
                  if (g.type === 'sale') {
                    const bIds = new Set(g.movements.map(m => m.batchId).filter(Boolean));
                    payments = productPaymentTx.filter(t => t.batchId && bIds.has(t.batchId));
                  } else if (g.type === 'order') {
                    const oIds = new Set(g.movements.map(m => String(m.orderId)).filter(Boolean));
                    payments = productPaymentTx.filter(t => t.orderId && oIds.has(String(t.orderId)));
                  } else if (g.type === 'appointment') {
                    const aIds = new Set(g.movements.map(m => String(m.appointmentId)).filter(Boolean));
                    payments = productPaymentTx.filter(t => t.appointmentId && aIds.has(String(t.appointmentId)));
                  }
                  const totalPaid = payments.reduce((s, t) => s + t.amount, 0);
                  const items = g.movements.map(m => {
                    const prod = (products || []).find(p => String(p.id) === String(m.productId));
                    return { productName: prod?.name || `#${m.productId}`, quantity: m.nettoQty };
                  });
                  const method = payments[0]?.method || null;
                  return { ...g, items, totalPaid, method };
                });

              // ── Subscription purchases (count as service payments) ──
              const subscriptionPurchases = customerTx.filter(t => t.type === 'payment' && t.category === 'subscription');
              const totalSubscriptionPaid = subscriptionPurchases.reduce((s, t) => s + (t.amount || 0), 0);
              // Subscription refunds
              const subscriptionRefunds = customerTx.filter(t => t.type === 'refund' && t.category === 'subscription_refund');
              const totalSubscriptionRefunded = subscriptionRefunds.reduce((s, t) => s + Math.abs(t.amount || 0), 0);

              // ── Totals ──
              // Charged sum counts only confirmed rows (non-confirmed show '—').
              const totalServiceCharged = serviceRows.reduce((s, r) => s + (r.isConfirmed ? r.charged : 0), 0);
              // Paid sum uses net_paid per row (payment - refund); subscription purchases
              // handled separately since their "payment" is one transaction, not per-visit.
              const totalServicePaid = serviceRows.reduce((s, r) => {
                if (r.appointment.paymentMethod === 'subscription') return s; // subscription visits counted via purchase total
                return s + (r.netPaid || 0);
              }, 0) + totalSubscriptionPaid - totalSubscriptionRefunded;
              const totalProductCharged = productGroups.reduce((s, g) => s + g.totalCharged, 0);
              const totalProductPaid = productGroups.reduce((s, g) => s + g.totalPaid, 0);
              const totalCharged = totalServiceCharged + totalProductCharged;
              // Header sums detail rows — keeps header and detail in sync (previous
              // behaviour used sqlStats.totalSpent which double-counted refunds
              // vs the detail display).
              const totalPaid = totalServicePaid + totalProductPaid;
              const balance = totalCharged - totalPaid;

              const fmtAptDate = (a) => {
                if (!a.date) return '—';
                const [y, mo, d] = a.date.split('-');
                return `${d}.${mo}.${y}`;
              };
              const fmtTxDateTime = (ts) => {
                const d = new Date(ts);
                return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
              };
              const methodIcon = (method) =>
                method === 'terminal' ? '💳' : method === 'transfer' ? '📱' : method === 'online' ? '💳' : '💵';

              return (
                <div className="tab-content">
                  <SectionTitle>{t('custModal.financialIndicators')}</SectionTitle>
                  <div className="financial-summary-cards">
                    <div className="fin-sum-card sold">
                      <span className="fin-sum-icon">💰</span>
                      <span className="fin-sum-label">{t('custModal.finCharged')}</span>
                      <span className="fin-sum-value">{totalCharged.toLocaleString('uk-UA')} ₴</span>
                    </div>
                    <div className="fin-sum-card paid">
                      <span className="fin-sum-icon">✅</span>
                      <span className="fin-sum-label">{t('custModal.finPaid')}</span>
                      <span className="fin-sum-value">{totalPaid.toLocaleString('uk-UA')} ₴</span>
                    </div>
                    <div className={`fin-sum-card balance ${balance > 0 ? 'neg' : balance < 0 ? 'neg' : 'pos'}`}>
                      <span className="fin-sum-icon">{balance === 0 ? '📊' : '📉'}</span>
                      <span className="fin-sum-label">{t('custModal.finBalance')}</span>
                      <span className="fin-sum-value">{balance.toLocaleString('uk-UA')} ₴</span>
                    </div>
                  </div>

                  {/* Нарахування за послуги */}
                  <SectionTitle>{t('custModal.serviceCharges')}</SectionTitle>
                  {serviceRows.length > 0 ? (
                    <div className="tx-table charges-tx-table">
                      <div className="tx-header">
                        <span>{t('custModal.date')}</span>
                        <span>{t('custModal.time')}</span>
                        <span>{t('custModal.serviceLabel')}</span>
                        <span>{t('custModal.finCharged')}</span>
                        <span>{t('custModal.finPaid')}</span>
                      </div>
                      {serviceRows.map(({ appointment: a, charged, isConfirmed, payment, netPaid }) => {
                        const aptStaff = (staff || []).find(s => s.id === a.staffId);
                        return (
                          <div key={a.id} className="tx-row">
                            <span className="tx-date">{fmtAptDate(a)}</span>
                            <span className="tx-time">{a.startTime || '—'}</span>
                            <span className="tx-service">{a.serviceName || '—'}{aptStaff ? ` · ${aptStaff.name}` : ''}</span>
                            <span className="tx-amount">{isConfirmed ? `${charged.toLocaleString('uk-UA')} ₴` : '—'}</span>
                            <span className="tx-amount">{netPaid > 0 ? `${netPaid.toLocaleString('uk-UA')} ₴ ${methodIcon(payment?.method)}` : (a.paymentMethod === 'subscription' ? '📋' : '—')}</span>
                          </div>
                        );
                      })}
                      {subscriptionPurchases.map(tx => {
                        const d = tx.createdAt ? new Date(tx.createdAt) : null;
                        const txDate = d ? `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}` : (tx.date || '—');
                        const txTime = d ? `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}` : '—';
                        return (
                        <div key={tx.id} className="tx-row" style={{ background: 'rgba(99,102,241,0.05)' }}>
                          <span className="tx-date">{txDate}</span>
                          <span className="tx-time">{txTime}</span>
                          <span className="tx-service">📋 {tx.description || t('custModal.subscriptionPurchase')}</span>
                          <span className="tx-amount">—</span>
                          <span className="tx-amount" style={{ color: '#22c55e' }}>{(tx.amount || 0).toLocaleString('uk-UA')} ₴ {methodIcon(tx.method)}</span>
                        </div>
                        );
                      })}
                      {subscriptionRefunds.map(tx => {
                        const d2 = tx.createdAt ? new Date(tx.createdAt) : null;
                        const txDate2 = d2 ? `${String(d2.getDate()).padStart(2,'0')}.${String(d2.getMonth()+1).padStart(2,'0')}.${d2.getFullYear()}` : (tx.date || '—');
                        const txTime2 = d2 ? `${String(d2.getHours()).padStart(2,'0')}:${String(d2.getMinutes()).padStart(2,'0')}` : '—';
                        return (
                        <div key={tx.id} className="tx-row" style={{ background: 'rgba(239,68,68,0.05)' }}>
                          <span className="tx-date">{txDate2}</span>
                          <span className="tx-time">{txTime2}</span>
                          <span className="tx-service">↩ {tx.description || t('custModal.subscriptionRefund')}</span>
                          <span className="tx-amount">—</span>
                          <span className="tx-amount" style={{ color: '#ef4444' }}>-{Math.abs(tx.amount || 0).toLocaleString('uk-UA')} ₴</span>
                        </div>
                        );
                      })}
                      <div className="tx-row tx-total-row">
                        <span></span><span></span>
                        <span style={{ textAlign: 'right', fontWeight: 600 }}>{t('custModal.total')}:</span>
                        <span className="tx-amount" style={{ fontWeight: 600 }}>{totalServiceCharged.toLocaleString('uk-UA')} ₴</span>
                        <span className="tx-amount" style={{ fontWeight: 600 }}>{totalServicePaid.toLocaleString('uk-UA')} ₴</span>
                      </div>
                    </div>
                  ) : (
                    <p style={{ color: 'var(--text-muted)', marginTop: 8, fontSize: 13 }}>{t('custModal.noChargesYet')}</p>
                  )}

                  {/* Нарахування за товари */}
                  <SectionTitle>{t('custModal.productCharges')}</SectionTitle>
                  {productGroups.length > 0 ? (
                    <div className="tx-table product-charges-table">
                      <div className="tx-header">
                        <span>{t('custModal.dateTime')}</span>
                        <span>{t('custModal.productLabel')}</span>
                        <span>{t('custModal.finCharged')}</span>
                        <span>{t('custModal.finPaid')}</span>
                      </div>
                      {productGroups.map(g => {
                        const label = g.items.map(i => `${i.productName} ×${i.quantity}`).join(', ');
                        return (
                          <div key={g.key} className="tx-row">
                            <span className="tx-date">{fmtTxDateTime(g.createdAt)}</span>
                            <span className="tx-service">{label}</span>
                            <span className="tx-amount">{Math.round(g.totalCharged).toLocaleString('uk-UA')} ₴</span>
                            <span className="tx-amount">{g.totalPaid ? `${g.totalPaid.toLocaleString('uk-UA')} ₴ ${methodIcon(g.method)}` : '—'}</span>
                          </div>
                        );
                      })}
                      <div className="tx-row tx-total-row">
                        <span></span>
                        <span style={{ textAlign: 'right', fontWeight: 600 }}>{t('custModal.total')}:</span>
                        <span className="tx-amount" style={{ fontWeight: 600 }}>{totalProductCharged.toLocaleString('uk-UA')} ₴</span>
                        <span className="tx-amount" style={{ fontWeight: 600 }}>{totalProductPaid.toLocaleString('uk-UA')} ₴</span>
                      </div>
                    </div>
                  ) : (
                    <p style={{ color: 'var(--text-muted)', marginTop: 8, fontSize: 13 }}>{t('custModal.noProductChargesYet')}</p>
                  )}
                </div>
              );
            })()}

            {activeTab === 'documents' && (() => {
              const phone = customer?.phone || form.phone;
              const docs = (customerDocuments || [])
                .filter(d => d.customerPhone === phone)
                .sort((a, b) => b.createdAt - a.createdAt);

              const handleDocFileChange = async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 2 * 1024 * 1024) {
                  toast.error(t('custModal.fileTooLarge'));
                  return;
                }
                const customerId = customer?.id || `new_${Date.now()}`;
                const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
                const path = `tenants/${tenantId || 'default'}/documents/customers/${customerId}/${Date.now()}_${safeName}`;
                setDocUploading(true);
                try {
                  let url;
                  if (file.type.startsWith('image/')) {
                    url = await uploadImage(file, path);
                  } else {
                    url = await uploadFile(file, path);
                  }
                  setDocForm(prev => ({ ...prev, file: { name: file.name, type: file.type, url } }));
                } catch (err) {
                  console.error('Doc upload failed:', err);
                  toast.error(t('custModal.uploadError', 'Помилка завантаження файлу'));
                } finally {
                  setDocUploading(false);
                }
              };

              const handleAddDoc = async () => {
                if (!docForm.name.trim()) { setDocNameError(true); return; }
                if (!phone) { toast.error(t('custModal.specifyPhoneFirst')); return; }
                const newDoc = {
                  id: Date.now(),
                  customerPhone: phone,
                  name: docForm.name.trim(),
                  text: docForm.text,
                  file: docForm.file,
                  createdAt: Date.now(),
                };
                // Optimistic append, then dual-write via saveDoc
                // (collection.js persists to Firestore + SQL). On failure
                // the next mount refetch picks up the true state.
                setCustomerDocuments(prev => [...prev, newDoc]);
                setDocForm({ name: '', text: '', file: null });
                setShowDocForm(false);
                try {
                  await saveDoc('customer_documents', newDoc);
                } catch (err) {
                  console.error('[customer_documents] save failed:', err);
                }
              };

              const handleDeleteDoc = async (id) => {
                setCustomerDocuments(prev => prev.filter(d => d.id !== id));
                setDocToDelete(null);
                try {
                  await removeDoc('customer_documents', id);
                } catch (err) {
                  console.error('[customer_documents] delete failed:', err);
                }
              };

              const fmtDate = (ts) => {
                const d = new Date(ts);
                return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
              };

              return (
                <div className="tab-content">
                  <SectionTitle>{t('custModal.customerDocuments')}</SectionTitle>

                  {!phone && (
                    <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>{t('custModal.specifyPhoneForDocs')}</p>
                  )}

                  {phone && (
                    <>
                      {docs.length === 0 && !showDocForm && (
                        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 }}>{t('custModal.noDocumentsYet')}</p>
                      )}

                      {docs.map(doc => (
                        <div key={doc.id} className="doc-card">
                          <div className="doc-card-header" onClick={() => setExpandedDoc(expandedDoc === doc.id ? null : doc.id)}>
                            <div className="doc-card-info">
                              <span className="doc-card-name">{doc.name}</span>
                              <span className="doc-card-date">{fmtDate(doc.createdAt)}</span>
                            </div>
                            <div className="doc-card-actions">
                              {isPreviewableDocx(doc) && (
                                <button type="button" className="doc-download-btn" title={t('custModal.previewDoc', 'Переглянути й надрукувати')}
                                  onClick={e => { e.stopPropagation(); setPreviewDoc(doc); }}>👁</button>
                              )}
                              {doc.file && (
                                <a href={doc.file.url || doc.file.data} download={doc.file.name} target="_blank" rel="noreferrer" className="doc-download-btn" title={t('custModal.download')} onClick={e => e.stopPropagation()}>⬇</a>
                              )}
                              <button type="button" className="doc-delete-btn" title={t('custModal.deleteDoc')} onClick={e => { e.stopPropagation(); setDocToDelete(doc.id); }}>✕</button>
                              <span className="doc-expand-icon">{expandedDoc === doc.id ? '▲' : '▼'}</span>
                            </div>
                          </div>
                          {expandedDoc === doc.id && (
                            <div className="doc-card-body">
                              {doc.text && <p className="doc-card-text">{doc.text}</p>}
                              {doc.file && (
                                <div className="doc-file-preview">
                                  {doc.file.type?.startsWith('image/') ? (
                                    <img src={doc.file.url || doc.file.data} alt={doc.file.name} className="doc-file-img" />
                                  ) : (
                                    <span className="doc-file-name">📎 {doc.file.name}</span>
                                  )}
                                </div>
                              )}
                              {!doc.text && !doc.file && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>{t('custModal.emptyDocument')}</p>}
                            </div>
                          )}
                        </div>
                      ))}

                      {showDocForm ? (
                        <div className="doc-add-form">
                          <div className="form-field">
                            <label className="field-label">{t('custModal.docName')} *</label>
                            <input
                              type="text"
                              value={docForm.name}
                              onChange={e => { setDocForm(prev => ({ ...prev, name: e.target.value })); setDocNameError(false); }}
                              placeholder={t('custModal.docNamePlaceholder')}
                              style={docNameError ? { borderColor: 'var(--error, #ef4444)' } : undefined}
                            />
                            {docNameError && <span className="field-error">{t('custModal.docNameRequired', 'Введіть назву документа')}</span>}
                          </div>
                          <div className="form-field">
                            <label className="field-label">{t('custModal.docText')}</label>
                            <textarea rows="3" value={docForm.text} onChange={e => setDocForm(prev => ({ ...prev, text: e.target.value }))} placeholder={t('custModal.docTextPlaceholder')} />
                          </div>
                          <div className="form-field">
                            <label className="field-label">{t('custModal.fileUpTo2MB')}</label>
                            {docForm.file ? (
                              <div className="doc-file-selected">
                                <span>📎 {docForm.file.name}</span>
                                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDocForm(prev => ({ ...prev, file: null }))}>✕</button>
                              </div>
                            ) : (
                              <button type="button" className="btn btn-outline btn-sm" onClick={() => docFileRef.current?.click()} disabled={docUploading}>
                                {docUploading ? '⏳' : '📎'} {t('custModal.attachFile')}
                              </button>
                            )}
                            <input ref={docFileRef} type="file" accept="image/*,.pdf,.doc,.docx" style={{ display: 'none' }} onChange={handleDocFileChange} />
                          </div>
                          <div className="doc-form-actions">
                            <button type="button" className="btn btn-success btn-sm" onClick={handleAddDoc}>✅ {t('custModal.addDoc')}</button>
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setShowDocForm(false); setDocForm({ name: '', text: '', file: null }); setDocNameError(false); }}>{t('custModal.cancel')}</button>
                          </div>
                        </div>
                      ) : (
                        <div className="doc-add-buttons">
                          <button type="button" className="btn btn-outline" onClick={() => setShowDocForm(true)}>+ {t('custModal.addDocument')}</button>
                          {customer?.id && hasPermission('clients_edit') && (
                            <button type="button" className="btn btn-outline" onClick={() => setTplModalOpen(true)}>📄 {t('custModal.docFromTemplate', 'Документ із шаблону')}</button>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {tplModalOpen && (
                    <DocumentFromTemplateModal
                      customer={customer}
                      visits={sqlDetail?.appointments || []}
                      locationId={activeLocationId || null}
                      canManage={hasPermission('documents_templates')}
                      onClose={() => setTplModalOpen(false)}
                      onCreated={(doc) => {
                        setCustomerDocuments(prev => [doc, ...prev.filter(d => d.id !== doc.id)]);
                        setTplModalOpen(false);
                        setPreviewDoc(doc);
                      }}
                    />
                  )}
                  {previewDoc && <DocxPreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} />}

                  {docToDelete && (
                    <div className="doc-confirm-overlay">
                      <div className="doc-confirm-card">
                        <p>{t('custModal.deleteThisDoc')}</p>
                        <div className="doc-confirm-actions">
                          <button type="button" className="btn btn-danger btn-sm" onClick={() => handleDeleteDoc(docToDelete)}>{t('custModal.deleteBtn')}</button>
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDocToDelete(null)}>{t('custModal.cancel')}</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {activeTab === 'loyalty' && customer && (() => {
              const pd = (loyaltyPrograms || []).find(p => p.id === 'default') || {};
              const pointsCfg = pd.points || {};
              const tiersCfg = pd.tiers || {};
              const subsCfg = pd.subscriptions || {};

              // Points data — server-fetched in customerLoyaltyBalance
              // effect above; null while in flight (treated as 0).
              const balance = customerLoyaltyBalance;
              const points = balance?.balance || 0;
              const totalEarned = balance?.totalEarned || 0;
              // customerLoyaltyTx is already customer-scoped + sorted DESC by
              // the server (loyalty-customer-ledger), newest first. Just cap
              // to 50 for the visible list.
              const txs = (customerLoyaltyTx || []).slice(0, 50);

              // Tier data — SQL-backed totalSpent. Falls back to the
              // customer-scoped sqlDetail.transactions (this customer only,
              // small) rather than the global DataContext transactions array.
              const totalSpent = sqlStats?.totalSpent ?? (sqlDetail?.transactions || [])
                .filter(tx => tx.type === 'payment' && tx.amount > 0)
                .reduce((s, tx) => s + tx.amount, 0);
              const sortedLevels = [...(tiersCfg.levels || [])].sort((a, b) => b.minSpent - a.minSpent);
              const currentTier = sortedLevels.find(l => totalSpent >= l.minSpent) || sortedLevels[sortedLevels.length - 1] || { name: '—', discount: 0 };
              const nextTier = sortedLevels.filter(l => l.minSpent > totalSpent).sort((a, b) => a.minSpent - b.minSpent)[0];

              // Subscriptions
              const custSubs = (customerSubscriptions || [])
                .filter(s => String(s.customerId) === String(customer.id))
                .sort((a, b) => (b.purchasedAt || 0) - (a.purchasedAt || 0));
              const planMap = new Map();
              (subscriptionPlans || []).forEach(p => planMap.set(String(p.id), p));

              const TX_COLORS = { earn: '#22c55e', redeem: '#ef4444', birthday: '#f59e0b', referral: '#8b5cf6', inactivity: '#3b82f6', manual: '#6b7280' };
              const TX_LABELS = { earn: 'loyalty.txEarn', redeem: 'loyalty.txRedeem', birthday: 'loyalty.txBirthday', referral: 'loyalty.txReferral', inactivity: 'loyalty.txInactivity', manual: 'loyalty.txManual' };

              const handleManualAdjust = (delta) => {
                if (!delta || !customer?.id) return;
                const pts = Number(delta);
                if (!pts) return;
                const now = Date.now();
                onLoyaltyBalancesChange(prev => {
                  const existing = prev.find(b => String(b.customerId) === String(customer.id));
                  if (existing) {
                    return prev.map(b => String(b.customerId) === String(customer.id) ? {
                      ...b,
                      balance: (b.balance || 0) + pts,
                      totalEarned: pts > 0 ? (b.totalEarned || 0) + pts : b.totalEarned,
                      totalRedeemed: pts < 0 ? (b.totalRedeemed || 0) + Math.abs(pts) : b.totalRedeemed,
                    } : b);
                  }
                  return [...prev, {
                    id: `bal_${customer.id}`, customerId: customer.id,
                    balance: Math.max(0, pts), totalEarned: pts > 0 ? pts : 0, totalRedeemed: pts < 0 ? Math.abs(pts) : 0,
                  }];
                });
                // Straight to the ledger (audit 2026-11-03): this went only to
                // onLoyaltyTransactionsChange, which App.js no longer passes —
                // the call threw and the adjustment was never recorded.
                apiFetch('/api/collection?name=loyalty_transactions', {
                  method: 'POST',
                  body: {
                    id: `lt_manual_${now}_${customer.id}`,
                    customerId: customer.id, type: 'manual', points: pts, createdAt: now,
                    note: pts > 0 ? t('loyalty.manualAdd') : t('loyalty.manualDeduct'),
                    // Every loyalty tx must carry an event linkage. Manual
                    // adjustments reference themselves via a deterministic id so
                    // history and reversals can find them.
                    referenceType: 'manual',
                    referenceId: `manual_${now}_${customer.id}`,
                  },
                }).catch(err => toast.error(`${t('custModal.error')}: ${err?.message || ''}`));
              };

              const formatDate = (ts) => {
                if (!ts) return '—';
                const d = new Date(ts);
                const pad = n => String(n).padStart(2, '0');
                return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
              };

              return (
                <div className="tab-content">
                  {/* Points section */}
                  {pointsCfg.enabled && (
                    <>
                      <SectionTitle>{t('loyalty.sectionPoints')}</SectionTitle>
                      <div className="loyalty-balance-card">
                        <div className="loyalty-balance-main">
                          <div className="loyalty-balance-points">
                            <span className="loyalty-balance-num">{points}</span>
                            <span className="loyalty-balance-label">{t('loyalty.pointsBalance')}</span>
                          </div>
                        </div>
                        <div className="loyalty-balance-stats">
                          <div><span className="loyalty-earned">+{totalEarned}</span> {t('loyalty.earned')}</div>
                          <div><span className="loyalty-redeemed">−{balance?.totalRedeemed || 0}</span> {t('loyalty.redeemed')}</div>
                        </div>
                      </div>
                      <div className="loyalty-manual-adjust">
                        <input type="number" id="loyalty-manual-pts" placeholder={t('loyalty.manualPlaceholder')} className="loyalty-manual-input" />
                        <button className="btn btn-outline btn-sm" onClick={() => {
                          const inp = document.getElementById('loyalty-manual-pts');
                          handleManualAdjust(inp.value);
                          inp.value = '';
                        }}>{t('loyalty.manualApply')}</button>
                      </div>
                      {txs.length > 0 && (
                        <>
                          <SectionTitle>{t('loyalty.history')}</SectionTitle>
                          <div className="loyalty-tx-list">
                            {txs.map(tx => (
                              <div key={tx.id} className="loyalty-tx-row">
                                <span className="loyalty-tx-date">{formatDate(tx.createdAt)}</span>
                                <span className="loyalty-type-badge" style={{ background: TX_COLORS[tx.type] || '#6b7280' }}>
                                  {t(TX_LABELS[tx.type] || 'loyalty.txManual')}
                                </span>
                                <span className={`loyalty-points-delta ${tx.points > 0 ? 'positive' : 'negative'}`}>
                                  {tx.points > 0 ? '+' : ''}{tx.points}
                                </span>
                                <span className="loyalty-tx-note">{tx.note || ''}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {/* Tiers section */}
                  {tiersCfg.enabled && (
                    <>
                      <SectionTitle>{t('loyalty.sectionTierStatus')}</SectionTitle>
                      <div className="loyalty-balance-card">
                        <div className="loyalty-balance-main">
                          <div className="loyalty-balance-points">
                            <span className="loyalty-balance-num">{totalSpent.toLocaleString()} грн</span>
                            <span className="loyalty-balance-label">{t('loyalty.colTotalSpent')}</span>
                          </div>
                          <div className="loyalty-balance-tier">
                            <span className="loyalty-tier-badge large">{currentTier.name}</span>
                            {currentTier.discount > 0 && (
                              <span className="loyalty-next-tier">{t('loyalty.tierDiscountLabel')}: {currentTier.discount}%</span>
                            )}
                            {nextTier && (
                              <span className="loyalty-next-tier">
                                {t('loyalty.nextTier')}: {nextTier.name} ({(nextTier.minSpent - totalSpent).toLocaleString()} грн)
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Subscriptions section */}
                  {subsCfg.enabled && (
                    <>
                      <SectionTitle>{t('loyalty.sectionSubscriptions')}</SectionTitle>
                      {customer?.id && (
                        <div style={{ marginBottom: 12 }}>
                          <select
                            style={{ marginRight: 8 }}
                            id="custModal_sellPlan"
                            defaultValue=""
                          >
                            <option value="">{t('loyalty.selectPlan')}</option>
                            {(subscriptionPlans || []).filter(p => p.enabled !== false).map(p => (
                              <option key={p.id} value={p.id}>{p.name} — {p.price} грн</option>
                            ))}
                          </select>
                          <button className="btn btn-primary btn-sm" onClick={async () => {
                            const sel = document.getElementById('custModal_sellPlan');
                            const planId = sel?.value;
                            if (!planId) return;
                            const plan = (subscriptionPlans || []).find(p => String(p.id) === planId);
                            if (!plan) return;
                            // Same server sale as LoyaltyPage (audit 2026-11-03): two
                            // separate writes here could leave a sale without its
                            // subscription, and the payment carried paymentMethod
                            // only — `method` was NULL in SQL, so it dropped out of
                            // the cash shift and the by-method totals.
                            try {
                              const { subscription } = await apiFetch('/api/subscriptions/purchase', {
                                method: 'POST',
                                body: { customerId: String(customer.id), planId: plan.id, method: 'cash', locationId: activeLocationId },
                              });
                              setCustomerSubscriptions(prev => [...prev, subscription]);
                              sel.value = '';
                            } catch (err) {
                              toast.error(`${t('custModal.error')}: ${err?.message || ''}`);
                            }
                          }}>{t('loyalty.sellSubscription')}</button>
                        </div>
                      )}
                      {custSubs.map(s => {
                        const plan = planMap.get(String(s.planId));
                        const isRefunded = !!s.refundedAt;
                        const isExpired = s.expiresAt && s.expiresAt < Date.now();
                        const isUsedUp = s.totalVisits > 0 && (s.usedVisits || 0) >= s.totalVisits;
                        const status = isRefunded ? 'refunded' : isExpired ? 'expired' : isUsedUp ? 'used_up' : 'active';
                        const colors = { active: '#22c55e', expired: '#ef4444', used_up: '#6b7280', refunded: '#f59e0b' };
                        return (
                          <div key={s.id} className="loyalty-sub-card">
                            <div className="loyalty-sub-card-header">
                              <strong>{plan?.name || '—'}</strong>
                              <span className="loyalty-type-badge" style={{ background: colors[status] }}>
                                {t(`loyalty.subStatus_${status}`)}
                              </span>
                              {status === 'active' && (
                                <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => {
                                  const planName = plan?.name || '—';
                                  if (!window.confirm(t('loyalty.refundConfirm').replace('{name}', planName))) return;
                                  const now = Date.now();
                                  // Optimistic + direct save (Phase A6).
                                  const refundedSub = { ...s, refundedAt: now };
                                  setCustomerSubscriptions(prev => prev.map(x => x.id === s.id ? refundedSub : x));
                                  saveDoc('customer_subscriptions', refundedSub).catch(err => console.error('[customer_subscriptions] refund save failed:', err));
                                  // sqlDetail.transactions is this customer's full history
                                  // (including subscription purchases beyond the 30-day
                                  // DataContext window) — safer than the shared array.
                                  const origTx = (sqlDetail?.transactions || []).find(tx => tx.subscriptionId === s.id && tx.amount > 0);
                                  // What was paid, not the plan's price today (audit 2026-11-03).
                                  const price = origTx ? Number(origTx.amount) : (plan?.price || 0);
                                  if (price > 0) {
                                    const refundMethod = origTx?.method || origTx?.paymentMethod || 'cash';
                                    const txId = `tx_refund_${now}_${Math.random().toString(36).slice(2, 6)}`;
                                    saveTransactions([{
                                      id: txId, type: 'refund', amount: -Number(price),
                                      customerId: String(customer.id),
                                      customerName: customer.name || '',
                                      customerPhone: customer.phone || '',
                                      date: new Date(now).toISOString().slice(0, 10),
                                      description: `${t('loyalty.refund')}: ${planName}`,
                                      category: 'subscription_refund',
                                      subscriptionId: s.id,
                                      method: refundMethod,
                                      createdAt: now,
                                    }], activeLocationId, txAuthor).catch(console.error);
                                  }
                                }}>↩️ {t('loyalty.refund')}</button>
                              )}
                            </div>
                            {s.totalVisits > 0 && (
                              <div className="loyalty-sub-progress">
                                <span>{s.usedVisits || 0}/{s.totalVisits} {t('loyalty.visits')}</span>
                                <div className="loyalty-progress-bar">
                                  <div style={{ width: `${Math.min(100, ((s.usedVisits || 0) / s.totalVisits) * 100)}%` }} />
                                </div>
                              </div>
                            )}
                            {s.expiresAt && (
                              <div className="loyalty-sub-expires">{t('loyalty.subExpires')}: {formatDate(s.expiresAt)}</div>
                            )}
                          </div>
                        );
                      })}
                      {custSubs.length === 0 && (
                        <div style={{ color: 'var(--text-muted)', padding: '8px 0' }}>{t('loyalty.noActiveSubs')}</div>
                      )}
                    </>
                  )}

                  {/* No active modules */}
                  {!pointsCfg.enabled && !tiersCfg.enabled && !(subsCfg.enabled && custSubs.length > 0) && (
                    <div style={{ color: 'var(--text-muted)', padding: '16px 0' }}>{t('loyalty.noActiveModules')}</div>
                  )}
                </div>
              );
            })()}

            {activeTab === 'analytics' && customer && (() => {
              if (sqlLoading) return <SqlLoadingPlaceholder />;
              const phone = customer?.phone || form.phone;

              // ── Appointments for services (SQL-backed) ──
              const doneApts = (sqlDetail?.appointments || []).filter(a =>
                a.status === 'confirmed'
              );
              // Build subscription visit price lookup
              const custId = String(customer?.id || '');
              const subVisitPriceMap = {};
              (customerSubscriptions || []).forEach(sub => {
                if (String(sub.customerId) !== custId && sub.customerId !== phone) return;
                const plan = (subscriptionPlans || []).find(p => String(p.id) === String(sub.planId));
                if (plan?.price > 0 && plan?.totalVisits > 0) {
                  subVisitPriceMap[sub.id] = Math.round(Number(plan.price) / Number(plan.totalVisits) * 100) / 100;
                }
              });

              const serviceTotal = doneApts.reduce((s, a) => {
                if (a.paymentMethod === 'subscription') {
                  return s + (a.subscriptionVisitPrice > 0 ? Number(a.subscriptionVisitPrice) : (a.subscriptionId && subVisitPriceMap[a.subscriptionId]) || 0);
                }
                return s + Number(a.servicePrice || 0) * (1 - (Number(a.discount || 0) / 100));
              }, 0);

              // ── Same logic as Financial tab — stockMovements for products ──
              const customerTx = sqlDetail?.transactions || [];
              const MU_OUTFLOW_TYPES = new Set(['sale', 'order', 'appointment']);
              const allOutflows = (sqlDetail?.stockMovements || []).filter(m =>
                MU_OUTFLOW_TYPES.has(m.type) || (m.type === 'writeoff' && m.appointmentId)
              );
              const cancelMoves = (sqlDetail?.stockMovements || []).filter(m =>
                m.type === 'sale_cancel' || m.type === 'order_cancel' || m.type === 'appointment_cancel' || m.type === 'return'
              );
              const returnedQty = {};
              cancelMoves.forEach(m => {
                let groupKey = '';
                if (m.type === 'sale_cancel' && m.originalBatchId) groupKey = `sale_${m.originalBatchId}`;
                else if (m.type === 'order_cancel' && m.orderId) groupKey = `order_${m.orderId}`;
                else if (m.type === 'return' && m.orderId) groupKey = `order_${m.orderId}`;
                else if (m.type === 'appointment_cancel' && m.appointmentId) groupKey = `apt_${m.appointmentId}`;
                if (!groupKey) return;
                const key = `${groupKey}_${m.productId}`;
                returnedQty[key] = (returnedQty[key] || 0) + (m.quantity || 0);
              });
              const resolvePhone = (m) => {
                if (m.appointmentId) {
                  const apt = (sqlDetail?.appointments || []).find(a => String(a.id) === String(m.appointmentId));
                  if (apt?.clientPhone) return apt.clientPhone;
                }
                const tx = customerTx.find(t => {
                  if (t.type !== 'product_sale' || !t.customerPhone) return false;
                  if (m.batchId && t.batchId) return t.batchId === m.batchId;
                  if (m.orderId && t.orderId) return String(t.orderId) === String(m.orderId);
                  return false;
                });
                return tx?.customerPhone || '';
              };
              const getUnitCharge = (m) => {
                if (m.unitPrice) return m.unitPrice * (1 - (m.discount || 0) / 100);
                if (m.type === 'appointment' && m.appointmentId) {
                  const tx = customerTx.find(t =>
                    t.type === 'product_sale' && String(t.appointmentId) === String(m.appointmentId)
                    && String(t.productId) === String(m.productId) && t.amount > 0
                  );
                  if (tx) return tx.amount / (tx.quantity || 1);
                }
                const prod = (products || []).find(p => String(p.id) === String(m.productId));
                return prod?.sellPrice || 0;
              };

              // Group outflows by type and calculate product totals
              let posSaleTotal = 0, orderSaleTotal = 0, aptMaterialTotal = 0;
              const prodCount = {};
              const productOutflowTimestamps = [];
              allOutflows.forEach(m => {
                if (resolvePhone(m) !== phone) return;
                const key = m.type === 'sale' ? `sale_${m.batchId || m.id}`
                  : m.type === 'order' ? `order_${m.orderId || m.id}`
                  : `apt_${m.appointmentId || m.id}`;
                const returnKey = `${key}_${m.productId}`;
                const returned = returnedQty[returnKey] || 0;
                const nettoQty = Math.max(0, (m.quantity || 0) - returned);
                if (nettoQty <= 0) return;
                const unitCharge = getUnitCharge(m);
                const charged = unitCharge * nettoQty;
                if (m.type === 'sale') posSaleTotal += charged;
                else if (m.type === 'order') orderSaleTotal += charged;
                else aptMaterialTotal += charged;
                // Count products for favorite
                prodCount[m.productId] = (prodCount[m.productId] || 0) + nettoQty;
                productOutflowTimestamps.push(m.createdAt);
              });
              const productTotal = posSaleTotal + orderSaleTotal + aptMaterialTotal;

              // ── Subscription purchases ──
              const subPurchases = customerTx.filter(t => t.type === 'payment' && t.category === 'subscription');
              const subRefunds = customerTx.filter(t => t.type === 'refund' && t.category === 'subscription_refund');
              const subscriptionTotal = subPurchases.reduce((s, t) => s + (t.amount || 0), 0) - subRefunds.reduce((s, t) => s + Math.abs(t.amount || 0), 0);

              // ── LTV & avg check (SQL-backed) ──
              const localLtv = serviceTotal + productTotal + subscriptionTotal;
              const ltv = sqlStats?.totalSpent != null ? sqlStats.totalSpent : localLtv;
              const sqlVisits = sqlStats?.totalVisits || 0;
              const txCount = sqlVisits > 0 ? sqlVisits : (doneApts.filter(a => a.paymentMethod !== 'subscription').length + Object.keys(prodCount).length + subPurchases.length);
              const avgCheck = txCount > 0 ? ltv / txCount : 0;

              // ── Frequency (SQL-backed when available) ──
              const avgFreqDays = sqlDetail?.avgFreqDays ?? (() => {
                const sd = doneApts.map(a => new Date(a.date + 'T00:00:00').getTime()).sort((a, b) => a - b);
                if (sd.length < 2) return 0;
                let gs = 0;
                for (let i = 1; i < sd.length; i++) gs += (sd[i] - sd[i - 1]) / 86400000;
                return Math.round(gs / (sd.length - 1));
              })();

              // ── Last activity (SQL-backed) ──
              const lastVisitTs = sqlStats?.lastVisitAt || (doneApts.length > 0 ? new Date(doneApts[0]?.date + 'T00:00:00').getTime() : null);
              const lastPurchaseTs = productOutflowTimestamps.length > 0 ? Math.max(...productOutflowTimestamps) : null;
              const lastActivityTs = Math.max(lastVisitTs || 0, lastPurchaseTs || 0) || null;
              const daysSinceLast = lastActivityTs
                ? Math.round((Date.now() - lastActivityTs) / (1000 * 60 * 60 * 24))
                : null;

              // ── Churn risk ──
              const churnRatio = (avgFreqDays > 0 && daysSinceLast !== null) ? daysSinceLast / avgFreqDays : 0;
              const churnLevel = !lastActivityTs ? 'none' : churnRatio >= 3 ? 'high' : churnRatio >= 2 ? 'medium' : 'low';

              // ── Favorite product (SQL-backed when available) ──
              const sqlFavProdId = sqlDetail?.favorites?.productId;
              const topProdId = sqlFavProdId || Object.entries(prodCount).sort((a, b) => b[1] - a[1])[0]?.[0];
              const topProd = topProdId ? (products || []).find(p => String(p.id) === String(topProdId)) : null;

              // ── Favorite service (SQL-backed when available) ──
              const sqlFavSvcName = sqlDetail?.favorites?.serviceName;
              const topSvc = sqlFavSvcName
                ? { name: sqlFavSvcName }
                : (() => {
                    const sc = {};
                    doneApts.forEach(a => { if (a.serviceId) sc[a.serviceId] = (sc[a.serviceId] || 0) + 1; });
                    const topId = Object.entries(sc).sort((a, b) => b[1] - a[1])[0]?.[0];
                    return topId ? (services || []).find(s => String(s.id) === topId) : null;
                  })();

              // ── Spending breakdown (SQL-backed when available) ──
              const bd = sqlStats?.breakdown;
              const breakdownItems = bd ? [
                { label: t('custModal.servicesLabel'), amount: bd.services || 0, color: '#3b82f6' },
                { label: t('custModal.subscriptionsLabel'), amount: bd.subscriptions || 0, color: '#f59e0b' },
                { label: t('custModal.posSourceLabel'), amount: bd.pos || 0, color: '#22c55e' },
                { label: t('custModal.orderSourceLabel'), amount: bd.orders || 0, color: '#8b5cf6' },
              ].filter(b => b.amount > 0) : [
                { label: t('custModal.servicesLabel'), amount: serviceTotal, color: '#3b82f6' },
                { label: t('custModal.subscriptionsLabel'), amount: subscriptionTotal, color: '#f59e0b' },
                { label: t('custModal.posSourceLabel'), amount: posSaleTotal, color: '#22c55e' },
                { label: t('custModal.orderSourceLabel'), amount: orderSaleTotal, color: '#8b5cf6' },
              ].filter(b => b.amount > 0);
              const breakdownMax = Math.max(...breakdownItems.map(b => b.amount), 1);

              // ── Month charts (SQL-backed when available) ──
              const monthLabels = [t('custModal.monthShortJan'),t('custModal.monthShortFeb'),t('custModal.monthShortMar'),t('custModal.monthShortApr'),t('custModal.monthShortMay'),t('custModal.monthShortJun'),t('custModal.monthShortJul'),t('custModal.monthShortAug'),t('custModal.monthShortSep'),t('custModal.monthShortOct'),t('custModal.monthShortNov'),t('custModal.monthShortDec')];
              const now = new Date();
              const visitBars = [];
              const purchaseBars = [];
              const sqlMV = sqlDetail?.monthlyVisits;
              const sqlMP = sqlDetail?.monthlyPurchases;
              for (let i = 5; i >= 0; i--) {
                const m = new Date(now.getFullYear(), now.getMonth() - i, 1);
                const mKey = `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}`;
                const mStart = m.getTime();
                const mEnd = new Date(m.getFullYear(), m.getMonth() + 1, 0, 23, 59, 59).getTime();
                visitBars.push({
                  label: monthLabels[m.getMonth()],
                  count: sqlMV ? (sqlMV.find(v => v.month === mKey)?.count || 0) : doneApts.filter(a => a.date && a.date.startsWith(mKey)).length,
                });
                purchaseBars.push({
                  label: monthLabels[m.getMonth()],
                  count: sqlMP ? (sqlMP.find(v => v.month === mKey)?.count || 0) : productOutflowTimestamps.filter(ts => ts >= mStart && ts <= mEnd).length,
                });
              }
              const visitMax = Math.max(...visitBars.map(b => b.count), 1);
              const purchaseMax = Math.max(...purchaseBars.map(b => b.count), 1);

              return (
                <div className="tab-content">
                  {/* Summary stats */}
                  <div className="ca-stats-grid">
                    <div className="ca-stat">
                      <span className="ca-stat-value">{Math.round(ltv).toLocaleString('uk-UA')} &#8372;</span>
                      <span className="ca-stat-label">{t('custModal.ltv')}</span>
                    </div>
                    <div className="ca-stat">
                      <span className="ca-stat-value">{Math.round(avgCheck).toLocaleString('uk-UA')} &#8372;</span>
                      <span className="ca-stat-label">{t('custModal.avgCheck')}</span>
                    </div>
                    <div className="ca-stat">
                      <span className="ca-stat-value">{daysSinceLast !== null ? `${daysSinceLast} ${t('custModal.days')}` : '—'}</span>
                      <span className="ca-stat-label">{t('custModal.lastActivity')}</span>
                    </div>
                    <div className="ca-stat">
                      <span className="ca-stat-value">{avgFreqDays > 0 ? `${avgFreqDays} ${t('custModal.days')}` : '—'}</span>
                      <span className="ca-stat-label">{t('custModal.visitFrequency')}</span>
                    </div>
                  </div>

                  {/* Favorites */}
                  <div className="ca-favorites">
                    <div className="ca-favorite-card">
                      <span className="ca-favorite-icon">⭐</span>
                      <span className="ca-favorite-value">{topProd?.name || '—'}</span>
                      <span className="ca-favorite-label">{t('custModal.favoriteProduct')}</span>
                    </div>
                    <div className="ca-favorite-card">
                      <span className="ca-favorite-icon">⭐</span>
                      <span className="ca-favorite-value">{topSvc?.name || '—'}</span>
                      <span className="ca-favorite-label">{t('custModal.favoriteService')}</span>
                    </div>
                  </div>

                  {/* Spending breakdown */}
                  {breakdownItems.length > 0 && (
                    <div className="ca-breakdown">
                      <div className="ca-chart-title">{t('custModal.spendingBreakdown')}</div>
                      {breakdownItems.map((b, i) => (
                        <div key={i} className="ca-breakdown-row">
                          <span className="ca-breakdown-label">{b.label}</span>
                          <div className="ca-breakdown-bar-bg">
                            <div className="ca-breakdown-bar" style={{ width: `${(b.amount / breakdownMax) * 100}%`, background: b.color }} />
                          </div>
                          <span className="ca-breakdown-amount">{Math.round(b.amount).toLocaleString('uk-UA')} ₴</span>
                          <span className="ca-breakdown-pct">{ltv > 0 ? Math.round(b.amount / ltv * 100) : 0}%</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Churn risk */}
                  {churnLevel !== 'none' && (
                    <div className={`ca-churn ca-churn--${churnLevel}`}>
                      <span className="ca-churn-icon">{churnLevel === 'high' ? '🔴' : churnLevel === 'medium' ? '🟡' : '🟢'}</span>
                      <span className="ca-churn-text">{t(`custModal.churn_${churnLevel}`)}</span>
                    </div>
                  )}

                  {/* Charts */}
                  <div className="ca-charts-row">
                    <div className="ca-chart-section">
                      <div className="ca-chart-title">{t('custModal.visitsByMonth')}</div>
                      <div className="ca-chart">
                        {visitBars.map((b, i) => (
                          <div key={i} className="ca-chart-col">
                            <div className="ca-chart-num">{b.count > 0 ? b.count : ''}</div>
                            <div className="ca-chart-bar-bg">
                              <div className="ca-chart-bar" style={{ height: `${Math.max((b.count / visitMax) * 100, 2)}%` }} />
                            </div>
                            <div className="ca-chart-lbl">{b.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="ca-chart-section">
                      <div className="ca-chart-title">{t('custModal.purchasesByMonth')}</div>
                      <div className="ca-chart">
                        {purchaseBars.map((b, i) => (
                          <div key={i} className="ca-chart-col">
                            <div className="ca-chart-num">{b.count > 0 ? b.count : ''}</div>
                            <div className="ca-chart-bar-bg">
                              <div className="ca-chart-bar ca-chart-bar--purchases" style={{ height: `${Math.max((b.count / purchaseMax) * 100, 2)}%` }} />
                            </div>
                            <div className="ca-chart-lbl">{b.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {editMode ? t('custModal.cancel') : t('custModal.close')}
            </button>
            <div className="modal-footer-right">
              {activeTab !== 'main' && (
                <button type="button" className="btn btn-outline"
                  onClick={() => {
                    const idx = tabs.findIndex(tb => tb.id === activeTab);
                    if (idx > 0) setActiveTab(tabs[idx - 1].id);
                  }}>← {t('custModal.back')}</button>
              )}
              {activeTab !== tabs[tabs.length - 1].id && (
                <button type="button" className="btn btn-primary"
                  onClick={() => {
                    const idx = tabs.findIndex(tb => tb.id === activeTab);
                    if (idx < tabs.length - 1) setActiveTab(tabs[idx + 1].id);
                  }}>{t('custModal.next')} →</button>
              )}
              {editMode ? (
                <button type="submit" className={`btn btn-success${saving ? ' btn-loading' : ''}`} disabled={saving || isLocked}>
                  {customer ? '💾 ' + t('custModal.save') : '✅ ' + t('custModal.addBtn')}
                </button>
              ) : customer && (
                <button type="button" className="btn btn-primary" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditMode(true); setActiveTab('main'); }}>
                  ✏️ {t('custModal.editBtn')}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CustomerModal;
